import { localEventDate, localEventParts, eventInstant, shiftEventDate } from '../../shared/eventTimezones';
import { EventTimezoneField } from './EventTimezoneField';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import PublishIcon from '@mui/icons-material/Publish';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { useDraftPersistence } from '../hooks/useDraftPersistence';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { lineupStatusOptions, matchClassificationOptions, matchSiteTypeOptions, matchStatusOptions } from '../data/options';
import { lookupLabel } from '../data/lookups';
import type { AvailabilityStatus, EventRecurrenceType, LookupState, MatchEventRecord, TeamEventRecord, TeamEventStatus, TeamEventType, TimeFormat } from '../types';
import { lookupTeamAccentColor } from '../utils/teamColors';
import { formatTime, toTimeInput } from '../utils/timeFormat';
import { groupMeReminderOptions, groupMeReminderSummary, normalizeGroupMeReminders } from '../utils/groupmeReminders';
import { SeasonTeamMultiSelect } from './SeasonTeamMultiSelect';
import { DraftRecoveryDialog } from './DraftRecoveryDialog';
import { GroupMeLineupSend } from './GroupMeLineupSend';
import { DraftSavedIndicator } from './DraftSavedIndicator';
import { groupGroupMeOnlyGoingParticipants, summarizeGroupMeOnlyRsvps } from '../utils/groupmeOnlyRsvp';
import { PracticeRotationPlanner } from './PracticeRotationPlanner';
import { MatchResults } from './MatchResults';
import { CalendarPublicationPanel } from './CalendarPublicationPanel';
import { BreadcrumbTrail } from './ux/BreadcrumbTrail';
import { buildBreadcrumbTrail, teamEventCanonicalContext } from '../utils/uxNavigation';
import { accessibleTeamSelection, restoreAccessibleTeamSelection, sameTeamSelection, shouldShowCaptainScopeEmpty } from '../utils/captainWorkflowScope';
import { summarizeEventGuests, type GuestAttendanceEntry } from '../utils/eventGuestAttendance';

type TeamEventsProps = {
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  refreshLookups: () => Promise<void>;
  timeFormat: TimeFormat;
  refreshToken?: number;
  readOnly?: boolean;
  initialEventId?: string | null;
  requestedFilter?: { teamId?: string | null; eventTypeFilter: 'Match' } | null;
  onEventOpened?: () => void;
  onRequestedFilterApplied?: () => void;
  onOpenMatchDetail?: (matchId: string) => void;
  onEditMatch?: (matchId: string) => void;
  onAddEvent?: () => void;
  onEditEvent?: (eventId: string, origin?: 'team-events' | 'team-event-detail') => void;
  onAddMatch?: () => void;
  onOpenLineup?: (matchId: string, context?: { teamId?: string | null }) => void;
  onOpenAvailability?: (teamId?: string | null) => void;
};

type Snack = {
  message: string;
  severity: 'success' | 'error' | 'warning';
};

type TeamOptionRow = {
  id: string;
  team_name: string;
  season_label: string | null;
  workspace_id: string;
  is_active: boolean;
  groupme_group_id?: string | null;
  groupme_group_name?: string | null;
  default_groupme_event_reminders?: number[] | null;
};

type TeamRosterCountRow = {
  team_id: string;
  player_id: string | null;
};

type EventForm = {
  timezone: string;
  timezone_manual_override: boolean;
  team_id: string;
  event_type: Exclude<TeamEventType, 'Match'>;
  title: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  facility_id: string;
  location_text: string;
  status: TeamEventStatus;
  groupme_event_title: string;
  groupme_event_description: string;
  groupme_event_image_url: string;
  groupme_reminder_mode: 'team_default' | 'custom';
  groupme_custom_reminders: number[];
  notes: string;
};

type TeamEventBulkField =
  | 'event_date'
  | 'event_type'
  | 'title'
  | 'start_time'
  | 'end_time'
  | 'facility_id'
  | 'location_text'
  | 'status'
  | 'description'
  | 'notes';

type TeamEventBulkDraft = Partial<Record<TeamEventBulkField, string>>;

type MatchBulkField =
  | 'match_date'
  | 'opponent_name'
  | 'match_site_type'
  | 'match_time'
  | 'warmup_time'
  | 'facility_id'
  | 'court_numbers'
  | 'match_status'
  | 'match_classification'
  | 'opponent_captain_names'
  | 'opponent_captain_email'
  | 'opponent_captain_phone'
  | 'opponent_co_captain_name'
  | 'opponent_co_captain_email'
  | 'opponent_co_captain_phone'
  | 'opponent_tennis_record_roster_url'
  | 'notes_summary';

type MatchBulkDraft = Partial<Record<MatchBulkField, string>>;

type RecurrenceForm = {
  recurrence_type: EventRecurrenceType;
  recurrence_start_date: string;
  recurrence_end_date: string;
  repeat_interval: number;
  days_of_week: number[];
  day_of_month: number;
};

type GroupMeEventSourceRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  groupme_event_id: string | null;
  event_name: string;
  parsed_event_date: string | null;
  parsed_event_date_source: 'none' | 'title' | 'groupme_payload' | 'manual';
  linked_entity_id: string | null;
  location_type: 'facility' | 'other' | 'unknown';
  facility_id: string | null;
  other_location_name: string | null;
  sync_status: string;
  sync_error: string | null;
  source_updated_at: string | null;
};

type GroupMeParticipantRow = {
  id: string;
  groupme_only_rsvp_event_id: string;
  groupme_display_name: string;
  normalized_display_name: string;
  latest_rsvp_status: string;
  source_updated_at: string | null;
  match_status: string;
  matched_player_id: string | null;
  manual_status_updated_at: string | null;
  manual_status_note: string | null;
  players?: { id?: string; first_name: string; last_name: string; sex: string | null; estimated_dynamic_rating?: number | null; utr_rating?: number | null; utr_singles_rating?: number | null; utr_doubles_rating?: number | null } | null;
};

type EventAttendanceRow = {
  id: string;
  event_id: string;
  player_id: string | null;
  display_name: string | null;
  groupme_display_name: string | null;
  rsvp_status: string;
  guest_count: number;
  response_source: string;
  match_status: string;
  manual_status_updated_at: string | null;
  manual_status_note: string | null;
  players?: { id?: string; first_name: string; last_name: string; sex: string | null; estimated_dynamic_rating?: number | null; utr_rating?: number | null; utr_singles_rating?: number | null; utr_doubles_rating?: number | null } | null;
};

type RsvpDisplayRow = GroupMeParticipantRow | (EventAttendanceRow & { latest_rsvp_status: string });

type ManualRsvpStatus = 'Going' | 'Not Going' | 'Maybe';
type ManualRsvpDraft = { playerId: string; status: ManualRsvpStatus; note: string };
type ManualRsvpSnapshot = ManualRsvpDraft & {
  sourceId: string;
  eventId: string;
  playerLabel: string | undefined;
  updatedAt: string;
};
type ReplayResult = {
  ok?: boolean;
  replayedWebhookEvents?: number;
  participantCount?: number;
  appliedAvailabilityCount?: number;
  preservedManualCount?: number;
  unmatchedParticipantCount?: number;
};

type GroupMeEventActionResult = {
  ok?: boolean;
  summary?: {
    published: number;
    updated: number;
    refreshed: number;
    unpublished?: number;
    skipped: number;
    failed: number;
  };
  results?: Array<{ status: string; reason?: string; kind: 'match' | 'event'; id: string; groupmeEventId?: string }>;
  error?: string;
};

type DateRangePreset = 'upcoming' | 'this_week' | 'next_30_days' | 'this_season' | 'past' | 'all' | 'custom';

type TeamEventsViewState = {
  teamFilters: string[];
  teamScopeMode?: 'default' | 'explicit';
  eventTypeFilter: string;
  dateRangePreset: DateRangePreset;
  dateFromFilter: string;
  dateToFilter: string;
  expandedEventId: string | null;
};

type MatchResultRow = {
  id?: string | null;
  match_event_id?: string | null;
  result_status: string | null;
  team_result_value?: string | null;
  our_team_score?: number | string | null;
  opponent_team_score?: number | string | null;
  updated_at?: string | null;
  updated_by?: string | null;
};

type MatchAvailabilityRow = {
  match_event_id: string;
  player_id: string | null;
  availability_status: AvailabilityStatus | string | null;
  players?: { id?: string; first_name: string | null; last_name: string | null; sex: string | null } | null;
};

type MatchAvailabilitySummary = {
  total: number;
  available: number;
  maybe: number;
  unknown: number;
  notAvailable: number;
  availablePlayers: MatchAvailabilityRow[];
};

type UnifiedEventRow =
  | { kind: 'team-event'; key: string; date: string; time: string | null; team_id: string; record: TeamEventRecord }
  | { kind: 'match'; key: string; date: string; time: string | null; team_id: string; record: MatchEventRecord };

const eventTypeOptions: { value: TeamEventType; label: string }[] = [
  { value: 'Match', label: 'Match' },
  { value: 'Practice', label: 'Practice' },
  { value: 'Social', label: 'Social Event' },
  { value: 'Meeting', label: 'Team Meeting' },
  { value: 'Other', label: 'Other' },
];

const teamEventTypeOptions = eventTypeOptions.filter((option): option is { value: Exclude<TeamEventType, 'Match'>; label: string } => option.value !== 'Match');

const dateRangeOptions: { value: DateRangePreset; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'this_week', label: 'This Week' },
  { value: 'next_30_days', label: 'Next 30 Days' },
  { value: 'this_season', label: 'This Season' },
  { value: 'past', label: 'Past Events' },
  { value: 'all', label: 'All Events' },
  { value: 'custom', label: 'Custom Range' },
];

const eventStatusOptions: { value: TeamEventStatus; label: string }[] = [
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Postponed', label: 'Postponed' },
  { value: 'Rescheduled', label: 'Rescheduled' },
];

const manualRsvpStatuses: ManualRsvpStatus[] = ['Going', 'Not Going', 'Maybe'];

const recurrenceTypeOptions: { value: EventRecurrenceType; label: string }[] = [
  { value: 'Does Not Repeat', label: 'Does Not Repeat' },
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
];

const daysOfWeekOptions = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const emptyForm: EventForm = {
  timezone: 'America/Chicago',
  timezone_manual_override: false,
  team_id: '',
  event_type: 'Practice',
  title: '',
  description: '',
  event_date: '',
  start_time: '',
  end_time: '',
  facility_id: '',
  location_text: '',
  status: 'Scheduled',
  groupme_event_title: '',
  groupme_event_description: '',
  groupme_event_image_url: '',
  groupme_reminder_mode: 'team_default',
  groupme_custom_reminders: [],
  notes: '',
};

const emptyRecurrenceForm: RecurrenceForm = {
  recurrence_type: 'Does Not Repeat',
  recurrence_start_date: '',
  recurrence_end_date: '',
  repeat_interval: 1,
  days_of_week: [],
  day_of_month: 1,
};

const maxOccurrences = 100;

const teamEventBulkFields: TeamEventBulkField[] = [
  'event_date',
  'event_type',
  'title',
  'start_time',
  'end_time',
  'facility_id',
  'location_text',
  'status',
  'description',
  'notes',
];

const teamEventBulkLabels: Record<TeamEventBulkField, string> = {
  event_date: 'Date',
  event_type: 'Event Type',
  title: 'Title',
  start_time: 'Start Time',
  end_time: 'End Time',
  facility_id: 'Facility',
  location_text: 'Location Text',
  status: 'Status',
  description: 'Description',
  notes: 'Notes',
};

function teamEventBulkCellKey(eventId: string, field: TeamEventBulkField): string {
  return `${eventId}:${field}`;
}

const matchBulkFields: MatchBulkField[] = [
  'match_date',
  'opponent_name',
  'match_site_type',
  'match_time',
  'warmup_time',
  'facility_id',
  'court_numbers',
  'match_status',
  'match_classification',
  'opponent_captain_names',
  'opponent_captain_email',
  'opponent_captain_phone',
  'opponent_co_captain_name',
  'opponent_co_captain_email',
  'opponent_co_captain_phone',
  'opponent_tennis_record_roster_url',
  'notes_summary',
];

const matchBulkLabels: Record<MatchBulkField, string> = {
  match_date: 'Date',
  opponent_name: 'Opponent',
  match_site_type: 'Home/Away',
  match_time: 'Match Time',
  warmup_time: 'Warm-up',
  facility_id: 'Facility',
  court_numbers: 'Courts',
  match_status: 'Match Status',
  match_classification: 'Classification',
  opponent_captain_names: 'Opposing Captain Name',
  opponent_captain_email: 'Opposing Captain Email',
  opponent_captain_phone: 'Opposing Captain Phone',
  opponent_co_captain_name: 'Opposing Co-Captain Name',
  opponent_co_captain_email: 'Opposing Co-Captain Email',
  opponent_co_captain_phone: 'Opposing Co-Captain Phone',
  opponent_tennis_record_roster_url: 'TennisRecord URL',
  notes_summary: 'Notes',
};

function matchBulkCellKey(matchId: string, field: MatchBulkField): string {
  return `${matchId}:${field}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function normalizeNullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeEmail(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function isValidEmailAddress(value: string | null | undefined): boolean {
  const email = normalizeEmail(value);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
}

function eventTypeLabel(value: string): string {
  return eventTypeOptions.find((option) => option.value === value)?.label || value;
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dateFromIso(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date: Date): Date {
  const next = new Date(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function dateRangeBounds(preset: DateRangePreset, customFrom: string, customTo: string): { from: string; to: string } {
  const today = new Date();
  const todayValue = toIsoDate(today);
  if (preset === 'upcoming') return { from: todayValue, to: '' };
  if (preset === 'this_week') return { from: toIsoDate(startOfWeek(today)), to: toIsoDate(addDays(startOfWeek(today), 6)) };
  if (preset === 'next_30_days') return { from: todayValue, to: toIsoDate(addDays(today, 30)) };
  if (preset === 'past') return { from: '', to: toIsoDate(addDays(today, -1)) };
  if (preset === 'custom') return { from: customFrom, to: customTo };
  return { from: '', to: '' };
}

function chronologicalTimeSortKey(value: string | null | undefined): string {
  // Events with no configured time stay at the end of their calendar day.
  return toTimeInput(value) || '99:99';
}

function eventEndDateTime(event: TeamEventRecord): Date | null {
  if (!event.event_date) return null;
  const time = event.end_time || event.start_time || '23:59';
  const end = localEventParts(event, time, event.timezone || 'America/Chicago');
  const value = new Date(Number(eventInstant(end.date, end.time, event.timezone)?.epochMilliseconds ?? NaN));
  return Number.isNaN(value.getTime()) ? null : value;
}

function shouldAutoCompleteTeamEvent(event: TeamEventRecord, now = new Date()): boolean {
  if (event.event_type === 'Match') return false;
  if (event.status !== 'Scheduled') return false;
  const endDateTime = eventEndDateTime(event);
  if (!endDateTime) return false;
  return now.getTime() - endDateTime.getTime() >= 48 * 60 * 60 * 1000;
}

function addMonthsClamped(date: Date, months: number, dayOfMonth: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(dayOfMonth, lastDay));
  return next;
}

function weeksBetween(start: Date, candidate: Date): number {
  const startWeek = addDays(start, -start.getDay());
  const candidateWeek = addDays(candidate, -candidate.getDay());
  return Math.floor((candidateWeek.getTime() - startWeek.getTime()) / 604_800_000);
}

function monthsBetween(start: Date, candidate: Date): number {
  return (candidate.getFullYear() - start.getFullYear()) * 12 + candidate.getMonth() - start.getMonth();
}

function generateOccurrenceDates(form: EventForm, recurrence: RecurrenceForm): string[] {
  if (recurrence.recurrence_type === 'Does Not Repeat') return [form.event_date].filter(Boolean);
  if (!recurrence.recurrence_start_date || !recurrence.recurrence_end_date) return [];
  const interval = Math.max(1, Number(recurrence.repeat_interval || 1));
  const start = dateFromIso(recurrence.recurrence_start_date);
  const end = dateFromIso(recurrence.recurrence_end_date);
  if (end < start) return [];
  const dates: string[] = [];

  if (recurrence.recurrence_type === 'Daily') {
    for (let cursor = start; cursor <= end && dates.length <= maxOccurrences; cursor = addDays(cursor, interval)) {
      dates.push(toIsoDate(cursor));
    }
  }

  if (recurrence.recurrence_type === 'Weekly') {
    for (let cursor = start; cursor <= end && dates.length <= maxOccurrences; cursor = addDays(cursor, 1)) {
      if (weeksBetween(start, cursor) % interval === 0 && recurrence.days_of_week.includes(cursor.getDay())) {
        dates.push(toIsoDate(cursor));
      }
    }
  }

  if (recurrence.recurrence_type === 'Monthly') {
    const firstCandidate = addMonthsClamped(new Date(start.getFullYear(), start.getMonth(), 1), 0, recurrence.day_of_month);
    let cursor = firstCandidate < start ? addMonthsClamped(firstCandidate, interval, recurrence.day_of_month) : firstCandidate;
    while (cursor <= end && dates.length <= maxOccurrences) {
      if (monthsBetween(start, cursor) % interval === 0) dates.push(toIsoDate(cursor));
      cursor = addMonthsClamped(cursor, interval, recurrence.day_of_month);
    }
  }

  return [...new Set(dates)].sort();
}

function statusColor(status: TeamEventStatus): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'Completed') return 'success';
  if (status === 'Cancelled') return 'error';
  if (status === 'Postponed' || status === 'Rescheduled') return 'warning';
  return 'info';
}

function participantName(participant: GroupMeParticipantRow | EventAttendanceRow): string {
  return participant.players
    ? `${participant.players.first_name} ${participant.players.last_name}`
    : ('display_name' in participant ? participant.display_name || participant.groupme_display_name : participant.groupme_display_name) || 'Unnamed attendee';
}

function rsvpChipSx(kind: 'men' | 'women' | 'unmatched') {
  if (kind === 'men') {
    return {
      bgcolor: 'rgba(25, 118, 210, 0.08)',
      borderColor: 'rgba(25, 118, 210, 0.45)',
      color: '#0d47a1',
      fontWeight: 700,
    };
  }
  if (kind === 'women') {
    return {
      bgcolor: 'rgba(194, 24, 91, 0.08)',
      borderColor: 'rgba(194, 24, 91, 0.45)',
      color: '#880e4f',
      fontWeight: 700,
    };
  }
  return {
    bgcolor: 'rgba(97, 97, 97, 0.08)',
    borderColor: 'rgba(97, 97, 97, 0.4)',
    color: '#212121',
    fontWeight: 700,
  };
}

function rsvpSummaryChipSx(kind: 'going' | 'maybe' | 'notGoing' | 'notResponded' | 'unmatched') {
  if (kind === 'going') return { bgcolor: 'success.main', color: '#fff', fontWeight: 800 };
  if (kind === 'maybe') return { bgcolor: '#fff3cd', color: '#5f4600', fontWeight: 800 };
  if (kind === 'notGoing') return { bgcolor: '#d32f2f', color: '#fff', fontWeight: 800 };
  if (kind === 'notResponded') return { bgcolor: '#212121', color: '#fff', fontWeight: 800 };
  return { bgcolor: 'rgba(97, 97, 97, 0.14)', color: '#212121', fontWeight: 800 };
}

function notRespondedCount(teamPlayerCount: number, summary: ReturnType<typeof summarizeGroupMeOnlyRsvps>): number {
  return Math.max(0, teamPlayerCount - summary.goingTotal - summary.maybeTotal - summary.notGoingTotal);
}

function optionLabel(options: { value: string; label: string }[], value: string | null | undefined): string {
  return options.find((option) => option.value === value)?.label || value || '';
}

function matchLocation(match: MatchEventRecord, lookups: LookupState): string {
  return lookupLabel(lookups, 'facilities', match.facility_id) || match.court_numbers || match.groupme_location_text || 'Location not set';
}

function matchTitle(match: MatchEventRecord): string {
  const site = match.match_site_type === 'Away' ? '@' : 'vs';
  return `${site} ${match.opponent_name || 'Opponent TBD'}`;
}

function matchResultLabel(result: MatchResultRow | undefined): string {
  if (!result) return 'Completed';
  const outcome = result.team_result_value || result.result_status || 'Completed';
  const score = result.our_team_score != null && result.opponent_team_score != null
    ? ` ${result.our_team_score}-${result.opponent_team_score}`
    : '';
  return `${outcome}${score}`;
}

function matchSummaryLabel(match: MatchEventRecord, summary: MatchAvailabilitySummary | undefined, result: MatchResultRow | undefined, occurrence?: { occurrence_state: string; result_completeness: string }): string {
  if (match.match_status === 'Completed') return matchResultLabel(result);
  if (occurrence?.occurrence_state === 'Past') return occurrence.result_completeness;
  const availability = summary ? `${summary.available}/${summary.total} Available` : 'Availability not loaded';
  return `${optionLabel(lineupStatusOptions, match.lineup_status || 'Not Started')} • ${availability}`;
}

function formattedTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function availabilitySummary(rows: MatchAvailabilityRow[]): MatchAvailabilitySummary {
  return rows.reduce<MatchAvailabilitySummary>((summary, row) => {
    summary.total += 1;
    if (row.availability_status === 'available') {
      summary.available += 1;
      summary.availablePlayers.push(row);
    } else if (row.availability_status === 'maybe') summary.maybe += 1;
    else if (row.availability_status === 'not_available') summary.notAvailable += 1;
    else summary.unknown += 1;
    return summary;
  }, { total: 0, available: 0, maybe: 0, unknown: 0, notAvailable: 0, availablePlayers: [] });
}

function matchAvailabilityGenderGroup(row: MatchAvailabilityRow): 'men' | 'women' | 'unmatched' {
  const normalizedSex = (row.players?.sex || '').trim().toLowerCase();
  if (normalizedSex === 'male' || normalizedSex === 'm') return 'men';
  if (normalizedSex === 'female' || normalizedSex === 'f') return 'women';
  return 'unmatched';
}

function matchAvailabilityPlayerName(row: MatchAvailabilityRow): string {
  const firstName = row.players?.first_name || '';
  const lastName = row.players?.last_name || '';
  return `${firstName} ${lastName}`.trim() || 'Unnamed player';
}

function isManualRsvpStatus(value: string | null | undefined): value is ManualRsvpStatus {
  return manualRsvpStatuses.includes(value as ManualRsvpStatus);
}

function participantGenderGroup(participant: RsvpDisplayRow): 'men' | 'women' | 'unmatched' {
  if (participant.match_status !== 'matched') return 'unmatched';
  const normalizedSex = (participant.players?.sex || '').trim().toLowerCase();
  if (normalizedSex === 'male' || normalizedSex === 'm') return 'men';
  if (normalizedSex === 'female' || normalizedSex === 'f') return 'women';
  return 'unmatched';
}

function groupRsvpParticipantsByGender(participants: RsvpDisplayRow[]) {
  return participants.reduce<{ men: RsvpDisplayRow[]; women: RsvpDisplayRow[]; unmatched: RsvpDisplayRow[] }>(
    (groups, participant) => {
      groups[participantGenderGroup(participant)].push(participant);
      return groups;
    },
    { men: [], women: [], unmatched: [] },
  );
}

function normalizeManualParticipantName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function isManualRsvp(participant: RsvpDisplayRow): boolean {
  if ('response_source' in participant) {
    return Boolean(participant.manual_status_updated_at) || participant.response_source === 'manual' || participant.response_source === 'manual_mapping';
  }
  if (!participant.manual_status_updated_at) return false;
  if (!participant.source_updated_at) return true;
  return new Date(participant.manual_status_updated_at).getTime() >= new Date(participant.source_updated_at).getTime();
}

function attendanceSummaryInput(participant: EventAttendanceRow) {
  return {
    latest_rsvp_status: participant.rsvp_status,
    match_status: participant.match_status,
    players: participant.players,
  };
}

function attendanceAsGroupable(participant: EventAttendanceRow) {
  return {
    ...participant,
    latest_rsvp_status: participant.rsvp_status,
  };
}

export function TeamEvents({
  lookups,
  defaultCaptainTeamIds,
  refreshLookups,
  timeFormat,
  refreshToken = 0,
  readOnly = false,
  initialEventId = null,
  requestedFilter = null,
  onEventOpened,
  onRequestedFilterApplied,
  onOpenMatchDetail,
  onEditMatch,
  onAddEvent,
  onEditEvent,
  onAddMatch,
  onOpenLineup,
  onOpenAvailability,
}: TeamEventsProps) {
  const auditUserId = useAuditUserId();
  const defaultViewState = useMemo<TeamEventsViewState>(() => ({
    teamFilters: defaultCaptainTeamIds,
    teamScopeMode: 'default',
    eventTypeFilter: '',
    dateRangePreset: 'upcoming',
    dateFromFilter: '',
    dateToFilter: '',
    expandedEventId: null,
  }), [defaultCaptainTeamIds]);
  const persistentViewState = usePersistentViewState<TeamEventsViewState>({
    userId: auditUserId,
    routeKey: 'team-events',
    defaultState: defaultViewState,
  });
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = persistentViewState;
  const [events, setEvents] = useState<TeamEventRecord[]>([]);
  const [matches, setMatches] = useState<MatchEventRecord[]>([]);
  const [matchAvailability, setMatchAvailability] = useState<Record<string, MatchAvailabilitySummary>>({});
  const [matchResults, setMatchResults] = useState<Record<string, MatchResultRow>>({});
  const [matchOccurrence, setMatchOccurrence] = useState<Record<string, { occurrence_state: string; result_completeness: string }>>({});
  const [scoreEditorMatchId, setScoreEditorMatchId] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamOptionRow[]>([]);
  const [teamRosterPlayerIds, setTeamRosterPlayerIds] = useState<Record<string, string[]>>({});
  const [eventTeamIdsByEventId, setEventTeamIdsByEventId] = useState<Record<string, string[]>>({});
  const [groupMeSources, setGroupMeSources] = useState<GroupMeEventSourceRow[]>([]);
  const [groupMeParticipants, setGroupMeParticipants] = useState<GroupMeParticipantRow[]>([]);
  const [eventAttendance, setEventAttendance] = useState<EventAttendanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkEditorSaving, setBulkEditorSaving] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState<Record<string, TeamEventBulkDraft>>({});
  const [matchBulkDrafts, setMatchBulkDrafts] = useState<Record<string, MatchBulkDraft>>({});
  const [bulkErrors, setBulkErrors] = useState<Record<string, string>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [addTypeDialogOpen, setAddTypeDialogOpen] = useState(false);
  const [practiceSourceId, setPracticeSourceId] = useState<string | null>(null);
  const [editing, setEditing] = useState<TeamEventRecord | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TeamEventRecord | null>(null);
  const [form, setForm] = useState<EventForm>(emptyForm);
  const [recurrence, setRecurrence] = useState<RecurrenceForm>(emptyRecurrenceForm);
  const [savedEventDraft, setSavedEventDraft] = useState({ form: emptyForm, recurrence: emptyRecurrenceForm });
  const [manualRsvpDrafts, setManualRsvpDrafts] = useState<Record<string, ManualRsvpDraft>>({});
  const [replayingSourceId, setReplayingSourceId] = useState<string | null>(null);
  const [groupMeActionKey, setGroupMeActionKey] = useState<string | null>(null);
  const [groupMeImageUploading, setGroupMeImageUploading] = useState(false);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [teamFilters, setTeamFilters] = useState<string[]>(savedViewState.teamFilters);
  const [teamScopeMode, setTeamScopeMode] = useState<'default' | 'explicit'>(savedViewState.teamScopeMode || 'explicit');
  const [eventTypeFilter, setEventTypeFilter] = useState(savedViewState.eventTypeFilter);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>(savedViewState.dateRangePreset || 'upcoming');
  const [dateFromFilter, setDateFromFilter] = useState(savedViewState.dateFromFilter || '');
  const [dateToFilter, setDateToFilter] = useState(savedViewState.dateToFilter || '');
  const [expandedEventId, setExpandedEventId] = useState<string | null>(savedViewState.expandedEventId);
  const restoredViewStateKey = useRef('');
  const appliedRequestedFilterKey = useRef('');
  const autoReplayStartedSourceIds = useRef<Set<string>>(new Set());
  const autoReplayStartedMatchIds = useRef<Set<string>>(new Set());
  const [viewStateReady, setViewStateReady] = useState(false);

  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);
  const groupMeSourceByEventId = useMemo(() => new Map(groupMeSources.map((source) => [source.linked_entity_id, source])), [groupMeSources]);
  const participantsBySourceId = useMemo(() => {
    const grouped = new Map<string, GroupMeParticipantRow[]>();
    groupMeParticipants.forEach((participant) => {
      const rows = grouped.get(participant.groupme_only_rsvp_event_id) || [];
      rows.push(participant);
      grouped.set(participant.groupme_only_rsvp_event_id, rows);
    });
    return grouped;
  }, [groupMeParticipants]);
  const attendanceByEventId = useMemo(() => {
    const grouped = new Map<string, EventAttendanceRow[]>();
    eventAttendance.forEach((row) => {
      const rows = grouped.get(row.event_id) || [];
      rows.push(row);
      grouped.set(row.event_id, rows);
    });
    return grouped;
  }, [eventAttendance]);
  const eventAudiencePlayerCount = useCallback((event: TeamEventRecord): number => {
    const associatedTeamIds = event.audience_scope === 'workspace'
      ? teams.filter((team) => team.workspace_id === event.workspace_id).map((team) => team.id)
      : eventTeamIdsByEventId[event.id] || [event.team_id];
    return new Set(associatedTeamIds.flatMap((teamId) => teamRosterPlayerIds[teamId] || [])).size;
  }, [eventTeamIdsByEventId, teamRosterPlayerIds, teams]);
  const practiceEvent = useMemo(() => events.find((event) => event.id === practiceSourceId) || null, [events, practiceSourceId]);
  const currentEventDraft = useMemo(() => ({ form, recurrence }), [form, recurrence]);
  const eventDraftPersistence = useDraftPersistence({
    userId: auditUserId,
    screenType: 'team-event',
    entityId: editing?.id || 'new',
    value: currentEventDraft,
    initialValue: savedEventDraft,
    enabled: dialogOpen && !readOnly,
  });
  const occurrenceDates = useMemo(() => generateOccurrenceDates(form, recurrence), [form, recurrence]);
  const accessibleTeamIds = useMemo(() => lookups.teams.map((team) => team.value), [lookups.teams]);
  const activeTeamIdSet = useMemo(() => new Set(teams.map((team) => team.id)), [teams]);
  const activeDateRange = useMemo(() => dateRangeBounds(dateRangePreset, dateFromFilter, dateToFilter), [dateFromFilter, dateRangePreset, dateToFilter]);
  const isMatchBulkMode = eventTypeFilter === 'Match';
  const bulkEditableTeamEvents = useMemo(
    () => events.filter((event) => activeTeamIdSet.has(event.team_id)),
    [activeTeamIdSet, events],
  );
  const bulkEditableMatches = useMemo(
    () => matches.filter((match) => activeTeamIdSet.has(match.team_id)),
    [activeTeamIdSet, matches],
  );
  const bulkEditableRowCount = isMatchBulkMode ? bulkEditableMatches.length : bulkEditableTeamEvents.length;
  const unifiedRows = useMemo<UnifiedEventRow[]>(() => {
    const teamEventRows = events.map((event): UnifiedEventRow => ({
      kind: 'team-event',
      key: `event:${event.id}`,
      date: localEventDate(event),
      time: event.start_time,
      team_id: event.team_id,
      record: event,
    }));
    const matchRows = matches.map((match): UnifiedEventRow => ({
      kind: 'match',
      key: `match:${match.id}`,
      date: localEventDate(match),
      time: match.match_time,
      team_id: match.team_id,
      record: match,
    }));
    return [...teamEventRows, ...matchRows].sort((a, b) => {
      return a.date.localeCompare(b.date)
        || chronologicalTimeSortKey(localEventParts(a.record, a.time).time).localeCompare(chronologicalTimeSortKey(localEventParts(b.record, b.time).time))
        || a.key.localeCompare(b.key);
    });
  }, [events, matches]);
  const scoreEditorMatch = useMemo(
    () => matches.find((match) => match.id === scoreEditorMatchId) || null,
    [matches, scoreEditorMatchId],
  );

  function bulkOriginalValue(event: TeamEventRecord, field: TeamEventBulkField): string {
    const value = event[field as keyof TeamEventRecord];
    if (field === 'start_time' || field === 'end_time') return toTimeInput(value as string | null | undefined);
    return value == null ? '' : String(value);
  }

  function matchBulkOriginalValue(match: MatchEventRecord, field: MatchBulkField): string {
    const value = match[field as keyof MatchEventRecord];
    if (field === 'match_time' || field === 'warmup_time') return toTimeInput(value as string | null | undefined);
    return value == null ? '' : String(value);
  }

  const bulkValue = (event: TeamEventRecord, field: TeamEventBulkField): string => (
    bulkDrafts[event.id]?.[field] ?? bulkOriginalValue(event, field)
  );
  const matchBulkValue = (match: MatchEventRecord, field: MatchBulkField): string => (
    matchBulkDrafts[match.id]?.[field] ?? matchBulkOriginalValue(match, field)
  );
  const isBulkCellChanged = (event: TeamEventRecord, field: TeamEventBulkField): boolean => (
    bulkValue(event, field) !== bulkOriginalValue(event, field)
  );
  const isMatchBulkCellChanged = (match: MatchEventRecord, field: MatchBulkField): boolean => (
    matchBulkValue(match, field) !== matchBulkOriginalValue(match, field)
  );
  const teamEventBulkPendingCellCount = useMemo(
    () => bulkEditableTeamEvents.reduce((count, event) => count + teamEventBulkFields.filter((field) => isBulkCellChanged(event, field)).length, 0),
    [bulkDrafts, bulkEditableTeamEvents],
  );
  const matchBulkPendingCellCount = useMemo(
    () => bulkEditableMatches.reduce((count, match) => count + matchBulkFields.filter((field) => isMatchBulkCellChanged(match, field)).length, 0),
    [bulkEditableMatches, matchBulkDrafts],
  );
  const bulkPendingCellCount = isMatchBulkMode ? matchBulkPendingCellCount : teamEventBulkPendingCellCount;

  const loadTeams = useCallback(async () => {
    if (accessibleTeamIds.length === 0) {
      setTeams([]);
      setMatches([]);
      setEventAttendance([]);
      setTeamRosterPlayerIds({});
      return;
    }
    const [teamsResult, rostersResult] = await Promise.all([
      supabase
        .from('teams')
        .select('id, team_name, season_label, workspace_id, is_active, groupme_group_id, groupme_group_name, default_groupme_event_reminders')
        .is('deleted_at', null)
        .eq('is_active', true)
        .in('id', accessibleTeamIds)
        .order('team_name'),
      supabase
        .from('team_rosters')
        .select('team_id, player_id')
        .is('deleted_at', null)
        .eq('is_active', true)
        .neq('roster_status', 'inactive')
        .in('team_id', accessibleTeamIds)
        .limit(5000),
    ]);
    if (teamsResult.error) {
      setSnack({ severity: 'error', message: teamsResult.error.message });
      return;
    }
    if (rostersResult.error) {
      setSnack({ severity: 'error', message: rostersResult.error.message });
      setTeamRosterPlayerIds({});
    } else {
      const countsByTeam = ((rostersResult.data || []) as unknown as TeamRosterCountRow[]).reduce<Record<string, Set<string>>>((counts, row) => {
        if (!row.player_id) return counts;
        if (!counts[row.team_id]) counts[row.team_id] = new Set<string>();
        counts[row.team_id].add(row.player_id);
        return counts;
      }, {});
      setTeamRosterPlayerIds(Object.fromEntries(Object.entries(countsByTeam).map(([teamId, players]) => [teamId, [...players]])));
    }
    setTeams((teamsResult.data || []) as unknown as TeamOptionRow[]);
  }, [accessibleTeamIds]);

  const loadEvents = useCallback(async () => {
    if (!viewStateReady || accessibleTeamIds.length === 0 || shouldShowCaptainScopeEmpty(teamFilters, defaultCaptainTeamIds, teamScopeMode)) {
      setEvents([]);
      setMatches([]);
      setMatchAvailability({});
      setMatchResults({});
      setEventTeamIdsByEventId({});
      return;
    }
    setLoading(true);
    const includeTeamEvents = eventTypeFilter !== 'Match';
    const includeMatches = !eventTypeFilter || eventTypeFilter === 'Match';
    let eventQuery = supabase
      .from('events')
      .select('*')
      .is('deleted_at', null)
      .neq('event_type', 'Match');

    let matchQuery = supabase
      .from('match_events')
      .select('*, match_results(id, match_event_id, result_status, team_result_value, our_team_score, opponent_team_score, updated_at, updated_by)')
      .is('deleted_at', null)
      .in('team_id', accessibleTeamIds);

    if (teamFilters.length > 0) {
      matchQuery = matchQuery.in('team_id', teamFilters);
    }
    if (eventTypeFilter && eventTypeFilter !== 'Match') eventQuery = eventQuery.eq('event_type', eventTypeFilter);
    if (activeDateRange.from) {
      eventQuery = eventQuery.gte('event_date', shiftEventDate(activeDateRange.from, -2));
      matchQuery = matchQuery.gte('match_date', shiftEventDate(activeDateRange.from, -2));
    }
    if (activeDateRange.to) {
      eventQuery = eventQuery.lte('event_date', shiftEventDate(activeDateRange.to, 2));
      matchQuery = matchQuery.lte('match_date', shiftEventDate(activeDateRange.to, 2));
    }

    const [eventResult, matchResult] = await Promise.all([
      includeTeamEvents
        ? eventQuery.order('event_date', { ascending: true }).order('start_time', { ascending: true }).limit(750)
        : Promise.resolve({ data: [], error: null }),
      includeMatches
        ? matchQuery.order('match_date', { ascending: true }).order('match_time', { ascending: true }).limit(750)
        : Promise.resolve({ data: [], error: null }),
    ]);
    setLoading(false);
    if (eventResult.error || matchResult.error) {
      setSnack({ severity: 'error', message: eventResult.error?.message || matchResult.error?.message || 'Unable to load events.' });
      return;
    }

    const inLocalRange = (record: MatchEventRecord | TeamEventRecord) => (!activeDateRange.from || localEventDate(record) >= activeDateRange.from) && (!activeDateRange.to || localEventDate(record) <= activeDateRange.to);
    const loadedEventRows = ((eventResult.data || []) as unknown as TeamEventRecord[]).filter(inLocalRange);
    const eventTeamResult = loadedEventRows.length > 0
      ? await supabase.from('event_teams').select('event_id, team_id').in('event_id', loadedEventRows.map((event) => event.id))
      : { data: [], error: null };
    let eventRows = loadedEventRows;
    if (eventTeamResult.error) {
      setSnack({ severity: 'warning', message: `Unable to load event teams. ${eventTeamResult.error.message}` });
      setEventTeamIdsByEventId(Object.fromEntries(loadedEventRows.map((event) => [event.id, event.audience_scope === 'workspace' ? [] : [event.team_id]])));
    } else {
      const teamIdsByEvent = ((eventTeamResult.data || []) as Array<{ event_id: string; team_id: string }>).reduce<Record<string, Set<string>>>((result, row) => {
        if (!result[row.event_id]) result[row.event_id] = new Set<string>();
        result[row.event_id].add(row.team_id);
        return result;
      }, {});
      loadedEventRows.forEach((event) => {
        if (!teamIdsByEvent[event.id]) teamIdsByEvent[event.id] = new Set<string>();
        if (event.audience_scope !== 'workspace') teamIdsByEvent[event.id].add(event.team_id);
      });
      setEventTeamIdsByEventId(Object.fromEntries(Object.entries(teamIdsByEvent).map(([eventId, teamIds]) => [eventId, [...teamIds]])));
      if (teamFilters.length > 0) {
        const filterSet = new Set(teamFilters);
        eventRows = loadedEventRows.filter((event) => {
          if (event.audience_scope === 'workspace') {
            return teamFilters.some((teamId) => teamById.get(teamId)?.workspace_id === event.workspace_id);
          }
          return [...(teamIdsByEvent[event.id] || [])].some((teamId) => filterSet.has(teamId));
        });
      }
    }
    const autoCompleteIds = eventRows.filter((event) => shouldAutoCompleteTeamEvent(event)).map((event) => event.id);
    if (autoCompleteIds.length > 0 && !readOnly) {
      const { error: autoCompleteError } = await supabase
        .from('events')
        .update({ status: 'Completed', updated_by: auditUserId || null })
        .in('id', autoCompleteIds);
      if (autoCompleteError) {
        setSnack({ severity: 'warning', message: autoCompleteError.message });
      }
    }
    const rows = eventRows.map((event) => autoCompleteIds.includes(event.id) ? { ...event, status: 'Completed' as TeamEventStatus } : event);
    const matchRows = ((matchResult.data || []) as unknown as Array<MatchEventRecord & { match_results?: MatchResultRow[] | MatchResultRow | null }>).filter(inLocalRange);
    const occurrenceResult = matchRows.length > 0
      ? await supabase.rpc('match_occurrence_states', { p_match_ids: matchRows.map((match) => match.id) })
      : { data: [], error: null };
    if (occurrenceResult.error) {
      setSnack({ severity: 'error', message: occurrenceResult.error.message });
      return;
    }
    setMatchOccurrence(Object.fromEntries(((occurrenceResult.data || []) as Array<{ match_event_id: string; occurrence_state: string; result_completeness: string }>).map((row) => [row.match_event_id, row])));
    setEvents(rows);
    setSelectedEvent((current) => {
      if (!current) return current;
      return rows.find((event) => event.id === current.id) || current;
    });
    setMatches(matchRows.map(({ match_results: _matchResults, ...match }) => match));
    setMatchResults(matchRows.reduce<Record<string, MatchResultRow>>((acc, match) => {
      const result = Array.isArray(match.match_results) ? match.match_results[0] : match.match_results;
      if (result) acc[match.id] = result;
      return acc;
    }, {}));

    const matchIds = matchRows.map((match) => match.id);
    if (matchIds.length === 0) {
      setMatchAvailability({});
    } else {
      const availabilityResult = await supabase
        .from('availability_records')
        .select('match_event_id, player_id, availability_status, players(id, first_name, last_name, sex)')
        .in('match_event_id', matchIds)
        .is('deleted_at', null)
        .limit(5000);
      if (availabilityResult.error) {
        setMatchAvailability({});
        setSnack({ severity: 'warning', message: availabilityResult.error.message });
      } else {
        const rowsByMatch = ((availabilityResult.data || []) as unknown as MatchAvailabilityRow[]).reduce<Record<string, MatchAvailabilityRow[]>>((acc, row) => {
          acc[row.match_event_id] ||= [];
          acc[row.match_event_id].push(row);
          return acc;
        }, {});
        setMatchAvailability(Object.fromEntries(Object.entries(rowsByMatch).map(([matchId, availabilityRows]) => [matchId, availabilitySummary(availabilityRows)])));
      }
    }

    const eventIds = rows.map((event) => event.id);
    if (eventIds.length === 0) {
      setGroupMeSources([]);
      setGroupMeParticipants([]);
      setEventAttendance([]);
      return;
    }
    const attendanceResult = await supabase
      .from('event_attendance')
      .select('id, event_id, player_id, display_name, groupme_display_name, rsvp_status, guest_count, response_source, match_status, manual_status_updated_at, manual_status_note, players(id, first_name, last_name, sex, estimated_dynamic_rating, utr_rating, utr_singles_rating, utr_doubles_rating)')
      .in('event_id', eventIds)
      .is('deleted_at', null)
      .limit(3000);
    if (attendanceResult.error) {
      setEventAttendance([]);
      if (!attendanceResult.error.message.includes('event_attendance')) {
        setSnack({ severity: 'error', message: attendanceResult.error.message });
      }
    } else {
      setEventAttendance((attendanceResult.data || []) as unknown as EventAttendanceRow[]);
    }
    const sourcesResult = await supabase
      .from('groupme_only_rsvp_events')
      .select('id, workspace_id, team_id, groupme_event_id, event_name, parsed_event_date, parsed_event_date_source, linked_entity_id, location_type, facility_id, other_location_name, sync_status, sync_error, source_updated_at')
      .eq('linked_entity_type', 'event')
      .in('linked_entity_id', eventIds)
      .limit(500);
    if (sourcesResult.error) {
      setGroupMeSources([]);
      setGroupMeParticipants([]);
      if (!sourcesResult.error.message.includes('groupme_only_rsvp_events')) {
        setSnack({ severity: 'error', message: sourcesResult.error.message });
      }
      return;
    }
    const sources = (sourcesResult.data || []) as unknown as GroupMeEventSourceRow[];
    setGroupMeSources(sources);
    if (sources.length === 0) {
      setGroupMeParticipants([]);
      return;
    }
    const participantsResult = await supabase
      .from('groupme_only_rsvp_participants')
      .select('id, groupme_only_rsvp_event_id, groupme_display_name, normalized_display_name, latest_rsvp_status, source_updated_at, match_status, matched_player_id, manual_status_updated_at, manual_status_note, players(id, first_name, last_name, sex, estimated_dynamic_rating, utr_rating, utr_singles_rating, utr_doubles_rating)')
      .in('groupme_only_rsvp_event_id', sources.map((source) => source.id))
      .limit(2000);
    if (participantsResult.error) {
      setGroupMeParticipants([]);
      setSnack({ severity: 'error', message: participantsResult.error.message });
      return;
    }
    setGroupMeParticipants((participantsResult.data || []) as unknown as GroupMeParticipantRow[]);
  }, [accessibleTeamIds, activeDateRange.from, activeDateRange.to, auditUserId, defaultCaptainTeamIds, eventTypeFilter, readOnly, refreshToken, teamById, teamFilters, teamScopeMode, viewStateReady]);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (readOnly || groupMeSources.length === 0) return;
    const sourcesToRefresh = groupMeSources.filter((source) => (
      source.groupme_event_id &&
      source.linked_entity_id &&
      !autoReplayStartedSourceIds.current.has(source.id)
    ));
    if (sourcesToRefresh.length === 0) return;

    sourcesToRefresh.forEach((source) => autoReplayStartedSourceIds.current.add(source.id));
    let cancelled = false;

    const refreshLinkedGroupMeRsvps = async () => {
      let refreshedCount = 0;
      let reappliedManualCount = 0;

      for (const source of sourcesToRefresh) {
        const manualSnapshots = collectManualRsvpSnapshots(source);
        const { error } = await replayGroupMeEventRsvpsCore(source);
        if (error) {
          if (!cancelled) setSnack({ severity: 'warning', message: `Unable to refresh GroupMe RSVPs for ${source.event_name}: ${error.message}` });
          continue;
        }
        refreshedCount += 1;

        for (const snapshot of manualSnapshots) {
          const manualResult = await reapplyManualRsvpSnapshot(snapshot);
          if (manualResult.error) {
            if (!cancelled) setSnack({ severity: 'warning', message: `Unable to reapply manual RSVP for ${snapshot.playerLabel || 'player'}: ${manualResult.error.message}` });
            continue;
          }
          reappliedManualCount += 1;
        }
      }

      if (!cancelled && refreshedCount > 0) {
        await loadEvents();
        if (reappliedManualCount > 0) {
          setSnack({ severity: 'success', message: `Refreshed ${refreshedCount} GroupMe event${refreshedCount === 1 ? '' : 's'} and reapplied ${reappliedManualCount} manual RSVP override${reappliedManualCount === 1 ? '' : 's'}.` });
        }
      }
    };

    void refreshLinkedGroupMeRsvps();

    return () => {
      cancelled = true;
    };
  }, [groupMeSources, readOnly, loadEvents]);

  useEffect(() => {
    if (readOnly || matches.length === 0) return;
    const matchesToRefresh = matches.filter((match) => (
      match.groupme_event_id &&
      !autoReplayStartedMatchIds.current.has(match.id)
    ));
    if (matchesToRefresh.length === 0) return;

    matchesToRefresh.forEach((match) => autoReplayStartedMatchIds.current.add(match.id));
    let cancelled = false;

    const refreshLinkedGroupMeMatchRsvps = async () => {
      let refreshedCount = 0;
      let preservedManualCount = 0;

      for (const match of matchesToRefresh) {
        const { data, error } = await supabase.rpc('replay_groupme_match_event_rsvps', {
          p_match_event_id: match.id,
        });
        if (error) {
          if (!cancelled) setSnack({ severity: 'warning', message: `Unable to refresh GroupMe RSVPs for ${match.opponent_name || 'match'}: ${error.message}` });
          continue;
        }
        const result = (data || {}) as ReplayResult;
        refreshedCount += 1;
        preservedManualCount += result.preservedManualCount ?? 0;
      }

      if (!cancelled && refreshedCount > 0) {
        await loadEvents();
        if (preservedManualCount > 0) {
          setSnack({ severity: 'success', message: `Refreshed ${refreshedCount} GroupMe match RSVP set${refreshedCount === 1 ? '' : 's'} and preserved ${preservedManualCount} captain override${preservedManualCount === 1 ? '' : 's'}.` });
        }
      }
    };

    void refreshLinkedGroupMeMatchRsvps();

    return () => {
      cancelled = true;
    };
  }, [matches, readOnly, loadEvents]);

  useEffect(() => {
    if (accessibleTeamIds.length === 0) return;
    const valid = restoreAccessibleTeamSelection(teamFilters, defaultCaptainTeamIds, accessibleTeamIds);
    if (teamFilters.length > 0 && accessibleTeamSelection(teamFilters, accessibleTeamIds).length === 0) setTeamScopeMode('default');
    if (!sameTeamSelection(valid, teamFilters)) setTeamFilters(valid);
  }, [accessibleTeamIds, defaultCaptainTeamIds, teamFilters]);

  useEffect(() => {
    if (teamScopeMode !== 'default' || accessibleTeamIds.length === 0) return;
    const scope = accessibleTeamSelection(defaultCaptainTeamIds, accessibleTeamIds);
    setTeamFilters((current) => sameTeamSelection(current, scope) ? current : scope);
  }, [accessibleTeamIds, defaultCaptainTeamIds, teamScopeMode]);

  useEffect(() => {
    if (!expandedEventId || unifiedRows.length === 0 || unifiedRows.some((row) => row.key === expandedEventId)) return;
    setExpandedEventId(null);
  }, [expandedEventId, unifiedRows]);

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTeamFilters(savedViewState.teamScopeMode === 'default' ? defaultCaptainTeamIds : savedViewState.teamFilters);
    setTeamScopeMode(savedViewState.teamScopeMode || 'explicit');
    setEventTypeFilter(savedViewState.eventTypeFilter);
    setDateRangePreset(savedViewState.dateRangePreset || 'upcoming');
    setDateFromFilter(savedViewState.dateFromFilter || '');
    setDateToFilter(savedViewState.dateToFilter || '');
    setExpandedEventId(savedViewState.expandedEventId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, defaultCaptainTeamIds, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!requestedFilter) {
      appliedRequestedFilterKey.current = '';
      return;
    }
    if (!viewStateReady) return;
    const requestedFilterKey = JSON.stringify(requestedFilter);
    if (appliedRequestedFilterKey.current === requestedFilterKey) return;
    appliedRequestedFilterKey.current = requestedFilterKey;
    setTeamFilters(requestedFilter.teamId ? [requestedFilter.teamId] : []);
    setTeamScopeMode('explicit');
    setEventTypeFilter(requestedFilter.eventTypeFilter);
    setDateRangePreset('upcoming');
    setDateFromFilter('');
    setDateToFilter('');
    setExpandedEventId(null);
    onRequestedFilterApplied?.();
  }, [onRequestedFilterApplied, requestedFilter, viewStateReady]);

  useEffect(() => {
    if (!initialEventId || !viewStateReady || accessibleTeamIds.length === 0) return;
    let cancelled = false;
    void supabase.from('events').select('team_id').eq('id', initialEventId).is('deleted_at', null).maybeSingle().then(({ data }) => {
      if (cancelled || !data?.team_id || !accessibleTeamIds.includes(data.team_id)) return;
      setTeamFilters([data.team_id]);
      setTeamScopeMode('explicit');
      setDateRangePreset('all');
    });
    return () => { cancelled = true; };
  }, [accessibleTeamIds, initialEventId, viewStateReady]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({
      teamFilters,
      teamScopeMode,
      eventTypeFilter,
      dateRangePreset,
      dateFromFilter,
      dateToFilter,
      expandedEventId,
    });
  }, [dateFromFilter, dateRangePreset, dateToFilter, eventTypeFilter, expandedEventId, setSavedViewState, teamFilters, teamScopeMode, viewStateKey, viewStateReady]);

  usePersistentScrollRestoration({
    userId: auditUserId,
    routeKey: 'team-events',
    ready: !loading,
  });

  const updateForm = (patch: Partial<EventForm>) => setForm((current) => ({ ...current, ...patch }));
  const uploadGroupMeImage = async (file: File | null) => {
    if (!file) return;
    if (!form.team_id) {
      setSnack({ severity: 'warning', message: 'Select a team before uploading a GroupMe image.' });
      return;
    }
    setGroupMeImageUploading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sign in again before uploading a GroupMe image.');
      const body = new FormData();
      body.append('teamId', form.team_id);
      body.append('image', file);
      const response = await fetch('/api/groupme/upload-image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      const result = await response.json() as { imageUrl?: string; error?: string };
      if (!response.ok || !result.imageUrl) throw new Error(result.error || 'Unable to upload image to GroupMe.');
      updateForm({ groupme_event_image_url: result.imageUrl });
      setSnack({ severity: 'success', message: 'Image uploaded to GroupMe.' });
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to upload image to GroupMe.' });
    } finally {
      setGroupMeImageUploading(false);
    }
  };
  const updateFormReminder = (index: number, nextValue: string) => {
    setForm((current) => {
      const reminders = normalizeGroupMeReminders(current.groupme_custom_reminders);
      const next = [...reminders];
      if (nextValue === '') next.splice(index, 1);
      else next[index] = Number(nextValue);
      return { ...current, groupme_custom_reminders: normalizeGroupMeReminders(next) };
    });
  };
  const updateRecurrence = (patch: Partial<RecurrenceForm>) => setRecurrence((current) => ({ ...current, ...patch }));

  const changeTeam = (teamId: string) => {
    const team = teamById.get(teamId);
    setForm((current) => ({
      ...current,
      team_id: teamId,
    }));
  };

  const updateBulkCell = (event: TeamEventRecord, field: TeamEventBulkField, value: string) => {
    setBulkDrafts((current) => ({
      ...current,
      [event.id]: {
        ...(current[event.id] || {}),
        [field]: value,
      },
    }));
    setBulkErrors((current) => {
      const next = { ...current };
      delete next[teamEventBulkCellKey(event.id, field)];
      return next;
    });
  };

  const updateMatchBulkCell = (match: MatchEventRecord, field: MatchBulkField, value: string) => {
    setMatchBulkDrafts((current) => ({
      ...current,
      [match.id]: {
        ...(current[match.id] || {}),
        [field]: value,
      },
    }));
    setBulkErrors((current) => {
      const next = { ...current };
      delete next[matchBulkCellKey(match.id, field)];
      return next;
    });
  };

  const validateBulkEditor = (): Record<string, string> => {
    const nextErrors: Record<string, string> = {};
    if (isMatchBulkMode) {
      bulkEditableMatches.forEach((match) => {
        if (!matchBulkDrafts[match.id]) return;
        const date = matchBulkValue(match, 'match_date');
        const opponent = matchBulkValue(match, 'opponent_name').trim();
        const matchTime = matchBulkValue(match, 'match_time');
        const warmupTime = matchBulkValue(match, 'warmup_time');
        const status = matchBulkValue(match, 'match_status');
        const classification = matchBulkValue(match, 'match_classification');
        const url = matchBulkValue(match, 'opponent_tennis_record_roster_url').trim();
        const captainEmail = matchBulkValue(match, 'opponent_captain_email').trim();
        const coCaptainEmail = matchBulkValue(match, 'opponent_co_captain_email').trim();
        if (!date) nextErrors[matchBulkCellKey(match.id, 'match_date')] = 'Date is required.';
        if (!opponent) nextErrors[matchBulkCellKey(match.id, 'opponent_name')] = 'Opponent is required.';
        if (matchTime && !toTimeInput(matchTime)) nextErrors[matchBulkCellKey(match.id, 'match_time')] = 'Match time is invalid.';
        if (warmupTime && !toTimeInput(warmupTime)) nextErrors[matchBulkCellKey(match.id, 'warmup_time')] = 'Warm-up time is invalid.';
        if (!matchStatusOptions.some((option) => option.value === status)) nextErrors[matchBulkCellKey(match.id, 'match_status')] = 'Choose a valid match status.';
        if (isMatchBulkCellChanged(match, 'match_status') && (status === 'Scores Entered' || status === 'Completed')) {
          nextErrors[matchBulkCellKey(match.id, 'match_status')] = 'Scores Entered and Completed are set by score submission.';
        }
        if (!matchClassificationOptions.some((option) => option.value === classification)) nextErrors[matchBulkCellKey(match.id, 'match_classification')] = 'Choose a valid classification.';
        if (url && !/^https?:\/\/(www\.)?tennisrecord\.com\/adult\/teamprofile\.aspx/i.test(url)) {
          nextErrors[matchBulkCellKey(match.id, 'opponent_tennis_record_roster_url')] = 'Use a TennisRecord adult team profile URL.';
        }
        if (captainEmail && !isValidEmailAddress(captainEmail)) {
          nextErrors[matchBulkCellKey(match.id, 'opponent_captain_email')] = 'Enter a valid email address.';
        }
        if (coCaptainEmail && !isValidEmailAddress(coCaptainEmail)) {
          nextErrors[matchBulkCellKey(match.id, 'opponent_co_captain_email')] = 'Enter a valid email address.';
        }
      });
      return nextErrors;
    }

    bulkEditableTeamEvents.forEach((event) => {
      if (!bulkDrafts[event.id]) return;
      const date = bulkValue(event, 'event_date');
      const title = bulkValue(event, 'title').trim();
      const eventType = bulkValue(event, 'event_type');
      const status = bulkValue(event, 'status');
      const startTime = bulkValue(event, 'start_time');
      const endTime = bulkValue(event, 'end_time');
      if (!date) nextErrors[teamEventBulkCellKey(event.id, 'event_date')] = 'Date is required.';
      if (!title) nextErrors[teamEventBulkCellKey(event.id, 'title')] = 'Title is required.';
      if (!teamEventTypeOptions.some((option) => option.value === eventType)) nextErrors[teamEventBulkCellKey(event.id, 'event_type')] = 'Choose a valid event type.';
      if (!eventStatusOptions.some((option) => option.value === status)) nextErrors[teamEventBulkCellKey(event.id, 'status')] = 'Choose a valid status.';
      if (startTime && !toTimeInput(startTime)) nextErrors[teamEventBulkCellKey(event.id, 'start_time')] = 'Start time is invalid.';
      if (endTime && !toTimeInput(endTime)) nextErrors[teamEventBulkCellKey(event.id, 'end_time')] = 'End time is invalid.';
      const normalizedStart = toTimeInput(startTime);
      const normalizedEnd = toTimeInput(endTime);
      if (normalizedStart && normalizedEnd && normalizedEnd < normalizedStart) {
        nextErrors[teamEventBulkCellKey(event.id, 'end_time')] = 'End time must be after start time.';
      }
    });
    return nextErrors;
  };

  const openBulkEditor = () => {
    if (readOnly || bulkEditableRowCount === 0) return;
    setBulkDrafts({});
    setMatchBulkDrafts({});
    setBulkErrors({});
    setBulkEditorOpen(true);
  };

  const cancelBulkEditor = () => {
    const entityLabel = isMatchBulkMode ? 'match' : 'team event';
    if (bulkPendingCellCount > 0 && !window.confirm(`Discard unsaved ${entityLabel} edits?`)) return;
    setBulkEditorOpen(false);
    setBulkDrafts({});
    setMatchBulkDrafts({});
    setBulkErrors({});
  };

  const saveBulkEditor = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    if (bulkPendingCellCount === 0) {
      setSnack({ severity: 'success', message: isMatchBulkMode ? 'No match updates to save.' : 'No team event updates to save.' });
      return;
    }
    const nextErrors = validateBulkEditor();
    setBulkErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setSnack({ severity: 'error', message: isMatchBulkMode ? 'Fix highlighted match cells before saving.' : 'Fix highlighted team event cells before saving.' });
      return;
    }

    setBulkEditorSaving(true);
    const pendingCount = bulkPendingCellCount;
    if (isMatchBulkMode) {
      const changedMatches = bulkEditableMatches.filter((match) => matchBulkFields.some((field) => isMatchBulkCellChanged(match, field)));
      const results = await Promise.all(changedMatches.map((match) => {
        const payload = matchBulkFields.reduce<Record<string, string | null>>((acc, field) => {
          if (!isMatchBulkCellChanged(match, field)) return acc;
          const value = matchBulkValue(match, field).trim();
          if (field === 'match_time' || field === 'warmup_time') acc[field] = value ? toTimeInput(value) || value : null;
          else if (field === 'facility_id') acc[field] = value || null;
          else if (field === 'opponent_captain_email' || field === 'opponent_co_captain_email') acc[field] = normalizeEmail(value) || null;
          else if (field === 'opponent_tennis_record_roster_url' || field === 'opponent_captain_names' || field === 'opponent_captain_phone' || field === 'opponent_co_captain_name' || field === 'opponent_co_captain_phone' || field === 'court_numbers' || field === 'notes_summary') acc[field] = value || null;
          else acc[field] = value;
          return acc;
        }, {});
        payload.updated_by = auditUserId || null;
        return supabase.from('match_events').update(payload).eq('id', match.id);
      }));
      setBulkEditorSaving(false);

      const error = results.find((result) => result.error)?.error;
      if (error) {
        setSnack({ severity: 'error', message: error.message });
        return;
      }

      setSnack({ severity: 'success', message: `${pendingCount} match update${pendingCount === 1 ? '' : 's'} saved.` });
      setBulkEditorOpen(false);
      setMatchBulkDrafts({});
      setBulkErrors({});
      await loadEvents();
      await refreshLookups();
      return;
    }

    const changedEvents = bulkEditableTeamEvents.filter((event) => teamEventBulkFields.some((field) => isBulkCellChanged(event, field)));
    const results = await Promise.all(changedEvents.map((event) => {
      const payload = teamEventBulkFields.reduce<Record<string, string | null>>((acc, field) => {
        if (!isBulkCellChanged(event, field)) return acc;
        const value = bulkValue(event, field).trim();
        if (field === 'start_time' || field === 'end_time') acc[field] = value ? toTimeInput(value) || value : null;
        else if (field === 'facility_id') acc[field] = value || null;
        else if (field === 'description' || field === 'location_text' || field === 'notes') acc[field] = value || null;
        else acc[field] = value;
        return acc;
      }, {});
      payload.updated_by = auditUserId || null;
      return supabase.from('events').update(payload).eq('id', event.id);
    }));
    setBulkEditorSaving(false);

    const error = results.find((result) => result.error)?.error;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    setSnack({ severity: 'success', message: `${pendingCount} team event update${pendingCount === 1 ? '' : 's'} saved.` });
    setBulkEditorOpen(false);
    setBulkDrafts({});
    setMatchBulkDrafts({});
    setBulkErrors({});
    await loadEvents();
    await refreshLookups();
  };

  const openCreateWithType = (eventType: Exclude<TeamEventType, 'Match'>) => {
    if (readOnly) return;
    const firstTeam = teams[0];
    const today = todayIso();
    setEditing(null);
    setSelectedEvent(null);
    const nextForm = {
      ...emptyForm,
      event_type: eventType,
      team_id: firstTeam?.id || '',
      event_date: today,
    };
    const nextRecurrence = {
      ...emptyRecurrenceForm,
      recurrence_start_date: today,
      recurrence_end_date: today,
      days_of_week: [dateFromIso(today).getDay()],
      day_of_month: dateFromIso(today).getDate(),
    };
    setForm(nextForm);
    setRecurrence(nextRecurrence);
    setSavedEventDraft({ form: nextForm, recurrence: nextRecurrence });
    setDialogOpen(true);
  };

  const openEdit = (event: TeamEventRecord) => {
    if (readOnly) return;
    setEditing(event);
    const nextRecurrence = emptyRecurrenceForm;
    const nextForm = {
      timezone: event.timezone || 'America/Chicago',
      timezone_manual_override: event.timezone_manual_override || false,
      team_id: event.team_id,
      event_type: event.event_type === 'Match' ? 'Practice' : event.event_type,
      title: event.title || '',
      description: event.description || '',
      event_date: event.event_date || '',
      start_time: toTimeInput(event.start_time),
      end_time: toTimeInput(event.end_time),
      facility_id: event.facility_id || '',
      location_text: event.location_text || '',
      status: event.status || 'Scheduled',
      groupme_event_title: event.groupme_event_title || '',
      groupme_event_description: event.groupme_event_description || '',
      groupme_event_image_url: event.groupme_event_image_url || '',
      groupme_reminder_mode: event.groupme_reminder_mode || 'team_default',
      groupme_custom_reminders: normalizeGroupMeReminders(event.groupme_custom_reminders),
      notes: event.notes || '',
    };
    setRecurrence(nextRecurrence);
    setForm(nextForm);
    setSavedEventDraft({ form: nextForm, recurrence: nextRecurrence });
    setDialogOpen(true);
  };

  const openDetail = (event: TeamEventRecord) => {
    setSelectedEvent(event);
    setDetailOpen(true);
    window.history.pushState({}, '', `/team-events/${event.id}`);
  };

  useEffect(() => {
    if (!initialEventId || events.length === 0) return;
    const event = events.find((candidate) => candidate.id === initialEventId);
    if (!event) return;
    setSelectedEvent(event);
    setDetailOpen(true);
    onEventOpened?.();
  }, [events, initialEventId, onEventOpened]);

  useEffect(() => {
    if (!initialEventId) return;
    setDateRangePreset('all');
  }, [initialEventId]);

  const manualDraftForSource = (sourceId: string): ManualRsvpDraft => (
    manualRsvpDrafts[sourceId] || { playerId: '', status: 'Going', note: '' }
  );

  const updateManualRsvpDraft = (sourceId: string, patch: Partial<ManualRsvpDraft>) => {
    setManualRsvpDrafts((current) => ({ ...current, [sourceId]: { ...manualDraftForSource(sourceId), ...patch } }));
  };

  const upsertTeamEventAttendance = async (
    eventId: string,
    draft: ManualRsvpDraft,
    playerLabel: string | undefined,
    groupMeSourceId?: string | null,
  ) => {
    const existing = (attendanceByEventId.get(eventId) || []).find((row) => row.player_id === draft.playerId);
    const event = events.find((candidate) => candidate.id === eventId);
    if (draft.status === 'Maybe' && event?.allow_maybe_rsvp === false && existing?.rsvp_status !== 'Maybe') {
      return { error: new Error('Maybe RSVPs are not allowed for this event. Choose Going or Not Going.') };
    }
    const now = new Date().toISOString();
    const payload = {
      event_id: eventId,
      player_id: draft.playerId,
      display_name: playerLabel || draft.playerId,
      attended: false,
      rsvp_status: draft.status,
      response_source: groupMeSourceId ? 'manual_mapping' : 'manual',
      response_note: draft.note.trim() || null,
      responded_at: now,
      match_status: 'matched',
      manual_status_updated_at: now,
      manual_status_updated_by: auditUserId,
      manual_status_note: draft.note.trim() || null,
      updated_by: auditUserId,
      ...(groupMeSourceId ? { groupme_only_rsvp_event_id: groupMeSourceId } : {}),
      ...(existing?.id ? {} : { created_by: auditUserId }),
    };

    return existing?.id
      ? supabase.from('event_attendance').update(payload).eq('id', existing.id)
      : supabase.from('event_attendance').insert(payload);
  };

  const collectManualRsvpSnapshots = (source: GroupMeEventSourceRow): ManualRsvpSnapshot[] => {
    if (!source.linked_entity_id) return [];
    const snapshotsByPlayer = new Map<string, ManualRsvpSnapshot>();
    const rememberSnapshot = (snapshot: ManualRsvpSnapshot) => {
      const current = snapshotsByPlayer.get(snapshot.playerId);
      if (!current || snapshot.updatedAt >= current.updatedAt) snapshotsByPlayer.set(snapshot.playerId, snapshot);
    };

    (attendanceByEventId.get(source.linked_entity_id) || []).forEach((row) => {
      if (!row.player_id || !row.manual_status_updated_at || !isManualRsvpStatus(row.rsvp_status)) return;
      rememberSnapshot({
        sourceId: source.id,
        eventId: source.linked_entity_id || '',
        playerId: row.player_id,
        playerLabel: row.display_name || lookupLabel(lookups, 'players', row.player_id),
        status: row.rsvp_status,
        note: row.manual_status_note || '',
        updatedAt: row.manual_status_updated_at,
      });
    });

    (participantsBySourceId.get(source.id) || []).forEach((participant) => {
      if (!participant.matched_player_id || !participant.manual_status_updated_at || !isManualRsvpStatus(participant.latest_rsvp_status)) return;
      rememberSnapshot({
        sourceId: source.id,
        eventId: source.linked_entity_id || '',
        playerId: participant.matched_player_id,
        playerLabel: lookupLabel(lookups, 'players', participant.matched_player_id) || participant.groupme_display_name,
        status: participant.latest_rsvp_status,
        note: participant.manual_status_note || '',
        updatedAt: participant.manual_status_updated_at,
      });
    });

    return Array.from(snapshotsByPlayer.values());
  };

  const reapplyManualRsvpSnapshot = async (snapshot: ManualRsvpSnapshot) => {
    const draft: ManualRsvpDraft = { playerId: snapshot.playerId, status: snapshot.status, note: snapshot.note };
    const normalizedName = normalizeManualParticipantName(snapshot.playerLabel || snapshot.playerId);
    const now = new Date().toISOString();
    const attendancePayload = {
      event_id: snapshot.eventId,
      player_id: snapshot.playerId,
      display_name: snapshot.playerLabel || snapshot.playerId,
      attended: false,
      rsvp_status: snapshot.status,
      response_source: 'manual_mapping',
      response_note: snapshot.note || null,
      responded_at: snapshot.updatedAt || now,
      match_status: 'matched',
      manual_status_updated_at: snapshot.updatedAt || now,
      manual_status_updated_by: auditUserId,
      manual_status_note: snapshot.note || null,
      groupme_only_rsvp_event_id: snapshot.sourceId,
      updated_by: auditUserId,
    };
    const attendanceUpdate = await supabase
      .from('event_attendance')
      .update(attendancePayload)
      .eq('event_id', snapshot.eventId)
      .eq('player_id', snapshot.playerId)
      .is('deleted_at', null)
      .select('id');
    if (attendanceUpdate.error) return attendanceUpdate;
    if ((attendanceUpdate.data || []).length === 0) {
      const attendanceInsert = await supabase
        .from('event_attendance')
        .insert({ ...attendancePayload, created_by: auditUserId });
      if (attendanceInsert.error) return attendanceInsert;
    }

    const participantPayload = {
      latest_rsvp_status: draft.status,
      source_updated_at: snapshot.updatedAt || now,
      matched_player_id: snapshot.playerId,
      match_status: 'matched',
      match_confidence: 1,
      mapping_source: 'manual_mapping',
      manual_status_updated_at: snapshot.updatedAt || now,
      manual_status_updated_by: auditUserId,
      manual_status_note: snapshot.note || null,
    };
    const participantUpdate = await supabase
      .from('groupme_only_rsvp_participants')
      .update(participantPayload)
      .eq('groupme_only_rsvp_event_id', snapshot.sourceId)
      .eq('matched_player_id', snapshot.playerId)
      .select('id');
    if (participantUpdate.error) return participantUpdate;
    if ((participantUpdate.data || []).length > 0) return { error: null };

    const participantNameUpdate = await supabase
      .from('groupme_only_rsvp_participants')
      .update(participantPayload)
      .eq('groupme_only_rsvp_event_id', snapshot.sourceId)
      .eq('normalized_display_name', normalizedName)
      .select('id');
    if (participantNameUpdate.error) return participantNameUpdate;
    if ((participantNameUpdate.data || []).length > 0) return { error: null };

    return supabase.from('groupme_only_rsvp_participants').insert({
      groupme_only_rsvp_event_id: snapshot.sourceId,
      groupme_display_name: snapshot.playerLabel || snapshot.playerId,
      normalized_display_name: normalizedName,
      ...participantPayload,
    });
  };

  const replayGroupMeEventRsvpsCore = async (source: GroupMeEventSourceRow) => {
    return supabase.rpc('replay_groupme_only_event_rsvps', {
      p_groupme_only_event_id: source.id,
    });
  };

  const saveManualRsvp = async (source: GroupMeEventSourceRow) => {
    const draft = manualDraftForSource(source.id);
    if (!draft.playerId) {
      setSnack({ severity: 'warning', message: 'Select a player before saving a manual RSVP.' });
      return;
    }
    const playerLabel = lookupLabel(lookups, 'players', draft.playerId);
    const normalizedName = normalizeManualParticipantName(playerLabel || draft.playerId);
    const sourceParticipants = participantsBySourceId.get(source.id) || [];
    const existing = sourceParticipants.find((participant) => participant.matched_player_id === draft.playerId)
      || sourceParticipants.find((participant) => participant.normalized_display_name === normalizedName);
    const now = new Date().toISOString();
    if (source.linked_entity_id) {
      const attendanceResult = await upsertTeamEventAttendance(source.linked_entity_id, draft, playerLabel, source.id);
      if (attendanceResult.error) {
        setSnack({ severity: 'error', message: attendanceResult.error.message.includes('event_attendance') ? 'Apply the Team Event attendance migration in this environment before saving attendees.' : attendanceResult.error.message });
        return;
      }
    }

    const payload = {
      latest_rsvp_status: draft.status,
      source_updated_at: now,
      matched_player_id: draft.playerId,
      match_status: 'matched',
      match_confidence: 1,
      mapping_source: 'manual_mapping',
      manual_status_updated_at: now,
      manual_status_updated_by: auditUserId,
      manual_status_note: draft.note.trim() || null,
    };
    const result = existing
      ? await supabase.from('groupme_only_rsvp_participants').update(payload).eq('id', existing.id)
      : await supabase.from('groupme_only_rsvp_participants').insert({
        groupme_only_rsvp_event_id: source.id,
        groupme_display_name: playerLabel || draft.playerId,
        normalized_display_name: normalizedName,
        ...payload,
      });
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    setSnack({ severity: 'success', message: `${playerLabel || 'Player'} marked ${draft.status}.` });
    await loadEvents();
  };

  const saveTeamEventAttendance = async (event: TeamEventRecord) => {
    const draft = manualDraftForSource(event.id);
    if (!draft.playerId) {
      setSnack({ severity: 'warning', message: 'Select a player before saving attendance.' });
      return;
    }
    const playerLabel = lookupLabel(lookups, 'players', draft.playerId);
    const result = await upsertTeamEventAttendance(event.id, draft, playerLabel);
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message.includes('event_attendance') ? 'Apply the Team Event attendance migration in this environment before saving attendees.' : result.error.message });
      return;
    }
    setSnack({ severity: 'success', message: `${playerLabel || 'Player'} marked ${draft.status}.` });
    await loadEvents();
  };

  const replayGroupMeEventRsvps = async (source: GroupMeEventSourceRow) => {
    if (!source.groupme_event_id) {
      setSnack({ severity: 'warning', message: 'This Team Event does not have a GroupMe event ID to replay.' });
      return;
    }

    const manualSnapshots = collectManualRsvpSnapshots(source);
    setReplayingSourceId(source.id);
    const { data, error } = await replayGroupMeEventRsvpsCore(source);
    setReplayingSourceId(null);

    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }

    for (const snapshot of manualSnapshots) {
      const manualResult = await reapplyManualRsvpSnapshot(snapshot);
      if (manualResult.error) {
        setSnack({ severity: 'error', message: manualResult.error.message });
        await loadEvents();
        return;
      }
    }

    const result = (data || {}) as ReplayResult;
    setSnack({
      severity: 'success',
      message: `Replayed ${result.replayedWebhookEvents ?? 0} stored GroupMe RSVP message${(result.replayedWebhookEvents ?? 0) === 1 ? '' : 's'}; ${result.participantCount ?? 0} participant${(result.participantCount ?? 0) === 1 ? '' : 's'} now captured; ${manualSnapshots.length} manual override${manualSnapshots.length === 1 ? '' : 's'} reapplied.`,
    });
    await loadEvents();
  };

  const clearManualRsvp = async (participant: GroupMeParticipantRow) => {
    if (readOnly) return;
    const { error } = await supabase
      .from('groupme_only_rsvp_participants')
      .update({
        manual_status_updated_at: null,
        manual_status_updated_by: null,
        manual_status_note: null,
      })
      .eq('id', participant.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Manual RSVP override cleared.' });
    await loadEvents();
  };

  const copyRsvpNames = async (status: string, rows: RsvpDisplayRow[]) => {
    const names = rows.map(participantName).filter((name) => name && name !== 'Unnamed attendee');
    if (names.length === 0) {
      setSnack({ severity: 'warning', message: `No ${status} names to copy.` });
      return;
    }

    try {
      await navigator.clipboard.writeText(names.join('\n'));
      setSnack({ severity: 'success', message: `Copied ${names.length} ${status} name${names.length === 1 ? '' : 's'}.` });
    } catch {
      setSnack({ severity: 'error', message: 'Unable to copy RSVP names.' });
    }
  };

  const validateForm = (): string | null => {
    if (!form.team_id) return 'Team is required.';
    if (!form.event_type) return 'Event Type is required.';
    if (!form.title.trim()) return 'Title is required.';
    if (!form.event_date) return 'Date is required.';
    if (!form.status) return 'Status is required.';
    if (form.start_time && form.end_time && form.end_time < form.start_time) return 'End Time must be after Start Time.';
    if (!editing && recurrence.recurrence_type !== 'Does Not Repeat') {
      if (!recurrence.recurrence_start_date) return 'Recurrence Start Date is required.';
      if (!recurrence.recurrence_end_date) return 'Recurrence End Date is required.';
      if (recurrence.recurrence_end_date < recurrence.recurrence_start_date) return 'Recurrence End Date must be on or after Recurrence Start Date.';
      if (Number(recurrence.repeat_interval) < 1) return 'Repeat Interval must be at least 1.';
      if (recurrence.recurrence_type === 'Weekly' && recurrence.days_of_week.length === 0) return 'Weekly recurrence requires at least one day of week.';
      if (recurrence.recurrence_type === 'Monthly' && (recurrence.day_of_month < 1 || recurrence.day_of_month > 31)) return 'Monthly recurrence requires a valid day of month.';
      if (occurrenceDates.length === 0) return 'The recurrence settings do not create any events.';
      if (occurrenceDates.length > maxOccurrences) return `This recurrence would create more than ${maxOccurrences} events. Please shorten the date range or increase the interval.`;
    }
    return null;
  };

  const saveEvent = async () => {
    if (readOnly) {
      setSnack({ severity: 'error', message: 'You have view-only access.' });
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setSnack({ severity: 'error', message: validationError });
      return;
    }
    const basePayload = {
      timezone: form.timezone,
      timezone_manual_override: form.timezone_manual_override,
      team_id: form.team_id,
      event_type: form.event_type,
      title: form.title.trim(),
      description: normalizeNullable(form.description),
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      facility_id: form.facility_id || null,
      location_text: normalizeNullable(form.location_text),
      status: form.status,
      groupme_event_title: normalizeNullable(form.groupme_event_title),
      groupme_event_description: normalizeNullable(form.groupme_event_description),
      groupme_event_image_url: normalizeNullable(form.groupme_event_image_url),
      groupme_reminder_mode: form.groupme_reminder_mode,
      groupme_custom_reminders: form.groupme_reminder_mode === 'custom' ? normalizeGroupMeReminders(form.groupme_custom_reminders) : null,
      visibility: 'Team',
      notes: normalizeNullable(form.notes),
      updated_by: auditUserId,
      ...(editing?.id ? {} : { created_by: auditUserId }),
    };

    if (!editing && recurrence.recurrence_type !== 'Does Not Repeat') {
      const seriesPayload = {
        timezone: basePayload.timezone,
        timezone_manual_override: basePayload.timezone_manual_override,
        team_id: basePayload.team_id,
        event_type: basePayload.event_type,
        title: basePayload.title,
        description: basePayload.description,
        facility_id: basePayload.facility_id,
        location_text: basePayload.location_text,
        start_time: basePayload.start_time,
        end_time: basePayload.end_time,
        notes: basePayload.notes,
        recurrence_type: recurrence.recurrence_type,
        recurrence_start_date: recurrence.recurrence_start_date,
        recurrence_end_date: recurrence.recurrence_end_date,
        repeat_interval: Number(recurrence.repeat_interval),
        days_of_week: recurrence.recurrence_type === 'Weekly' ? recurrence.days_of_week : null,
        day_of_month: recurrence.recurrence_type === 'Monthly' ? recurrence.day_of_month : null,
        created_by: auditUserId,
        updated_by: auditUserId,
      };
      const seriesResult = await supabase.from('event_recurrence_series').insert(seriesPayload).select('id').single();
      if (seriesResult.error) {
        setSnack({ severity: 'error', message: seriesResult.error.message });
        return;
      }
      const recurrenceSeriesId = String((seriesResult.data as { id: string }).id);
      const eventRows = occurrenceDates.map((eventDate) => ({
        ...basePayload,
        event_date: eventDate,
        recurrence_series_id: recurrenceSeriesId,
      }));
      const eventResult = await supabase.from('events').insert(eventRows);
      if (eventResult.error) {
        setSnack({ severity: 'error', message: eventResult.error.message });
        return;
      }
      setDialogOpen(false);
      setSavedEventDraft(currentEventDraft);
      eventDraftPersistence.clearDraft();
      setSnack({ severity: 'success', message: `${occurrenceDates.length} ${eventTypeLabel(form.event_type).toLowerCase()} event${occurrenceDates.length === 1 ? '' : 's'} created.` });
      await loadEvents();
      await refreshLookups();
      return;
    }

    const payload = {
      ...basePayload,
      event_date: form.event_date,
    };
    const result = editing?.id
      ? await supabase.from('events').update(payload).eq('id', editing.id)
      : await supabase.from('events').insert(payload).select('id').single();
    if (result.error) {
      setSnack({ severity: 'error', message: result.error.message });
      return;
    }
    setDialogOpen(false);
    setSavedEventDraft(currentEventDraft);
    eventDraftPersistence.clearDraft();
    setSnack({ severity: 'success', message: 'Team event saved.' });
    await loadEvents();
    await refreshLookups();
  };

  const updateStatus = async (event: TeamEventRecord, status: TeamEventStatus) => {
    if (readOnly) return;
    const { error } = await supabase
      .from('events')
      .update({ status, updated_by: auditUserId })
      .eq('id', event.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: status === 'Cancelled' ? 'Event cancelled.' : status === 'Completed' ? 'Event marked completed.' : 'Event updated.' });
    setDetailOpen(false);
    await loadEvents();
  };

  const markMatchScoresSubmitted = async (match: MatchEventRecord, result: MatchResultRow) => {
    if (readOnly || result.result_status !== 'Confirmed') return;
    const submittedAt = new Date().toISOString();
    let query = supabase
      .from('match_results')
      .update({
        result_status: 'Submitted',
        updated_at: submittedAt,
        updated_by: auditUserId,
      });
    query = result.id ? query.eq('id', result.id) : query.eq('match_event_id', match.id).is('deleted_at', null);
    const { error } = await query;
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setMatchResults((current) => ({
      ...current,
      [match.id]: {
        ...result,
        result_status: 'Submitted',
        updated_at: submittedAt,
        updated_by: auditUserId,
      },
    }));
    setSnack({ severity: 'success', message: 'Results marked as submitted.' });
  };

  const softDeleteEvent = async (event: TeamEventRecord) => {
    if (readOnly) return;
    const linkedWarning = event.groupme_event_id
      ? ' This will also unpublish the linked GroupMe event. Republishing later will create a new GroupMe event.'
      : '';
    if (!window.confirm(`Soft delete ${event.title}?${linkedWarning}`)) return;
    if (event.groupme_event_id) {
      const unpublished = await unpublishRowFromGroupMe(
        { kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event },
        { confirm: false, reason: 'Unpublished because the CourtCaptain team event was deleted.', reload: false },
      );
      if (!unpublished) return;
    }
    const { error } = await supabase
      .from('events')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: auditUserId,
        delete_reason: 'Deleted from Team Events UI',
      })
      .eq('id', event.id);
    if (error) {
      setSnack({ severity: 'error', message: error.message });
      return;
    }
    setSnack({ severity: 'success', message: 'Team event deleted.' });
    setDetailOpen(false);
    await loadEvents();
  };

  const softDeleteFutureSeries = async (event: TeamEventRecord) => {
    if (readOnly) return;
    if (!event.recurrence_series_id) return;
    if (!window.confirm(`Delete future occurrences for ${event.title}? Past and completed occurrences will be preserved.`)) return;
    const now = new Date().toISOString();
    const today = todayIso();
    const [seriesResult, eventsResult] = await Promise.all([
      supabase
        .from('event_recurrence_series')
        .update({
          deleted_at: now,
          deleted_by: auditUserId,
          delete_reason: 'Deleted future recurrence series from Team Events UI',
        })
        .eq('id', event.recurrence_series_id),
      supabase
        .from('events')
        .update({
          deleted_at: now,
          deleted_by: auditUserId,
          delete_reason: 'Deleted future recurrence occurrence from Team Events UI',
        })
        .eq('recurrence_series_id', event.recurrence_series_id)
        .gte('event_date', today)
        .neq('status', 'Completed')
        .is('deleted_at', null),
    ]);
    if (seriesResult.error || eventsResult.error) {
      setSnack({ severity: 'error', message: seriesResult.error?.message || eventsResult.error?.message || 'Unable to delete recurrence series.' });
      return;
    }
    setSnack({ severity: 'success', message: 'Future recurring events deleted.' });
    setDetailOpen(false);
    await loadEvents();
  };

  const clearFilters = () => {
    setTeamFilters([]);
    setTeamScopeMode('explicit');
    setEventTypeFilter('');
    setDateRangePreset('upcoming');
    setDateFromFilter('');
    setDateToFilter('');
    setSavedViewState({ ...defaultViewState, teamFilters: [], teamScopeMode: 'explicit' });
  };

  const facilityOrLocation = (event: TeamEventRecord): string => {
    const facility = lookupLabel(lookups, 'facilities', event.facility_id);
    return facility || event.location_text || 'Location not set';
  };

  const toggleExpanded = (rowKey: string) => {
    setExpandedEventId((current) => (current === rowKey ? null : rowKey));
  };

  const openPracticeRotation = (event: TeamEventRecord) => {
    setPracticeSourceId(event.id);
  };

  const renderGoingChips = (rows: Array<EventAttendanceRow & { latest_rsvp_status: string }>, kind: 'men' | 'women' | 'unmatched') => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {rows.map((participant) => (
        <Chip
          key={participant.id}
          size="small"
          variant="outlined"
          label={`${participantName(participant)}${participant.guest_count > 0 ? ` (+${participant.guest_count} guest${participant.guest_count === 1 ? '' : 's'})` : ''}${kind === 'unmatched' && participant.match_status === 'matched' ? ' (Unspecified)' : ''}`}
          sx={rsvpChipSx(kind)}
        />
      ))}
    </Stack>
  );

  const renderRsvpSummaryChips = (summary: ReturnType<typeof summarizeGroupMeOnlyRsvps>, teamPlayerCount: number, unmatchedCount: number, guestSummary?: ReturnType<typeof summarizeEventGuests>, allowMaybeRsvp = true) => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`${summary.goingTotal} Going`} sx={rsvpSummaryChipSx('going')} />
      {guestSummary && <Chip size="small" label={`${guestSummary.guests} Guests`} variant="outlined" color="success" />}
      {guestSummary && <Chip size="small" label={`${guestSummary.totalAttending} Total Attending`} variant="outlined" color="success" />}
      <Chip size="small" label={`${summary.maybeTotal} ${allowMaybeRsvp ? 'Maybe' : 'Needs Confirmation'}`} sx={rsvpSummaryChipSx('maybe')} />
      <Chip size="small" label={`${summary.notGoingTotal} Not Going`} sx={rsvpSummaryChipSx('notGoing')} />
      <Chip size="small" label={`${notRespondedCount(teamPlayerCount, summary)} Not Responded`} sx={rsvpSummaryChipSx('notResponded')} />
      <Chip size="small" label={`${unmatchedCount} Unmatched`} sx={rsvpSummaryChipSx('unmatched')} />
    </Stack>
  );

  const renderGuestChips = (entries: GuestAttendanceEntry[]) => entries.length > 0 ? (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Guests ({entries.length})</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {entries.map((guest) => <Chip key={guest.id} size="small" variant="outlined" label={guest.name} />)}
      </Stack>
    </Box>
  ) : null;

  const renderDetailRsvpChips = (rows: RsvpDisplayRow[], kind: 'men' | 'women' | 'unmatched') => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {rows.map((participant) => (
        <Chip
          key={participant.id}
          size="small"
          variant="outlined"
          label={`${participantName(participant)}${isManualRsvp(participant) ? ' (Manual)' : kind === 'unmatched' && participant.match_status === 'matched' ? ' (Unspecified)' : kind === 'unmatched' ? ' (Unmatched)' : ''}`}
          sx={rsvpChipSx(kind)}
          onDelete={'groupme_only_rsvp_event_id' in participant && isManualRsvp(participant) && !readOnly ? () => void clearManualRsvp(participant) : undefined}
        />
      ))}
    </Stack>
  );

  const renderAttendancePanel = (
    event: TeamEventRecord,
    source: GroupMeEventSourceRow | undefined,
    attendanceRows: EventAttendanceRow[],
  ) => {
    const summary = summarizeGroupMeOnlyRsvps(attendanceRows.map(attendanceSummaryInput));
    const guestSummary = summarizeEventGuests(attendanceRows);
    const sortedParticipants = [...attendanceRows]
      .sort((a, b) => participantName(a).localeCompare(participantName(b), undefined, { sensitivity: 'base' }))
      .map(attendanceAsGroupable);
    const goingGroups = groupGroupMeOnlyGoingParticipants(sortedParticipants);
    const hasGoingPlayers = goingGroups.men.length + goingGroups.women.length + goingGroups.unmatched.length > 0;
    const showPracticeAction = event.event_type === 'Practice';
    const attendanceDraft = manualDraftForSource(event.id);
    const teamPlayerCount = eventAudiencePlayerCount(event);

    return (
      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(27, 94, 32, 0.025)' }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Event RSVPs
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(source?.groupme_event_id || event.groupme_event_id) ? (
                <>
                  <Tooltip title="Fetch the latest structured RSVP state from GroupMe">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        disabled={readOnly || groupMeActionKey === `refresh:team-event:${event.id}`}
                        onClick={() => void refreshRowGroupMeRsvps({ kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event })}
                      >
                        Refresh RSVPs
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Update the linked GroupMe event details from CourtCaptain">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PublishIcon />}
                        disabled={readOnly || groupMeActionKey === `update:team-event:${event.id}`}
                        onClick={() => void updateRowGroupMeEvent({ kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event })}
                      >
                        Update GroupMe
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : !readOnly && (
                <Tooltip title={groupMePublishEligibility({ kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event }).reason}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PublishIcon />}
                      disabled={!groupMePublishEligibility({ kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event }).eligible || groupMeActionKey === `publish:team-event:${event.id}`}
                      onClick={() => void publishRowToGroupMe({ kind: 'team-event', key: `team-event:${event.id}`, date: event.event_date, time: event.start_time, team_id: event.team_id, record: event })}
                    >
                      Publish to GroupMe
                    </Button>
                  </span>
                </Tooltip>
              )}
              {showPracticeAction && (
                <Tooltip title="Open Practice Rotation for this Team Event">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => openPracticeRotation(event)}
                    >
                      Practice Rotation
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Stack>

          {renderRsvpSummaryChips(summary, teamPlayerCount, goingGroups.unmatched.length, guestSummary, event.allow_maybe_rsvp !== false)}

          {!source && attendanceRows.length === 0 && (
            <Alert severity="info">
              Add attendees to enable a useful Practice Rotation roster for this Team Event.
            </Alert>
          )}
          {renderGuestChips(guestSummary.goingGuestEntries)}

          {!hasGoingPlayers ? (
            <Typography variant="body2" color="text.secondary">
              No players are currently marked Going.
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {goingGroups.men.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Men ({goingGroups.men.length})</Typography>
                  {renderGoingChips(goingGroups.men, 'men')}
                </Box>
              )}
              {goingGroups.women.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Women ({goingGroups.women.length})</Typography>
                  {renderGoingChips(goingGroups.women, 'women')}
                </Box>
              )}
              {goingGroups.unmatched.length > 0 && (
                <Box sx={{ gridColumn: { lg: goingGroups.men.length || goingGroups.women.length ? '1 / -1' : 'auto' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Unmatched ({goingGroups.unmatched.length})</Typography>
                  {renderGoingChips(goingGroups.unmatched, 'unmatched')}
                </Box>
              )}
            </Box>
          )}

          {!readOnly && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.72)' }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Add/Change RSVPs</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(150px, 0.35fr) minmax(220px, 1fr) auto' }, gap: 1, alignItems: 'center' }}>
                  <FormControl size="small">
                    <InputLabel>Player</InputLabel>
                    <Select
                      label="Player"
                      value={attendanceDraft.playerId}
                      onChange={(change) => updateManualRsvpDraft(event.id, { playerId: change.target.value })}
                    >
                      <MenuItem value="">Select player</MenuItem>
                      {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={attendanceDraft.status}
                      onChange={(change) => updateManualRsvpDraft(event.id, { status: change.target.value as ManualRsvpStatus })}
                    >
                      <MenuItem value="Going">Going</MenuItem>
                      {event.allow_maybe_rsvp !== false && <MenuItem value="Maybe">Maybe</MenuItem>}
                      <MenuItem value="Not Going">Not Going</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Note"
                    value={attendanceDraft.note}
                    onChange={(change) => updateManualRsvpDraft(event.id, { note: change.target.value })}
                    placeholder="Optional"
                  />
                  <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => void saveTeamEventAttendance(event)}>
                    Save
                  </Button>
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    );
  };

  const renderMatchAvailabilityChips = (rows: MatchAvailabilityRow[], kind: 'men' | 'women' | 'unmatched') => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {rows.map((row) => (
        <Chip
          key={`${row.match_event_id}:${row.player_id || matchAvailabilityPlayerName(row)}`}
          size="small"
          variant="outlined"
          label={matchAvailabilityPlayerName(row)}
          sx={rsvpChipSx(kind)}
        />
      ))}
    </Stack>
  );

  const renderMatchExpandedPanel = (match: MatchEventRecord) => {
    const summary = matchAvailability[match.id] || { total: 0, available: 0, maybe: 0, unknown: 0, notAvailable: 0, availablePlayers: [] };
    const availableGroups = summary.availablePlayers.reduce<{ men: MatchAvailabilityRow[]; women: MatchAvailabilityRow[]; unmatched: MatchAvailabilityRow[] }>(
      (groups, row) => {
        groups[matchAvailabilityGenderGroup(row)].push(row);
        return groups;
      },
      { men: [], women: [], unmatched: [] },
    );
    const result = matchResults[match.id];
    const occurrence = matchOccurrence[match.id];
    const timeline = [
      { label: 'Scheduled', done: true },
      { label: 'Lineup Published', done: Boolean(match.lineup_published_at) },
      { label: 'Scores Entered', done: match.match_status === 'Scores Entered' || result?.result_status === 'Submitted' },
      { label: 'Scores Submitted', done: result?.result_status === 'Submitted' },
    ];

    return (
      <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: 'rgba(25, 118, 210, 0.025)' }}>
        <Stack spacing={1.5}>
          {match.groupme_unpublished_at && !match.groupme_event_id && (
            <Alert severity={match.groupme_unpublish_reason?.includes('deleted in GroupMe') ? 'warning' : 'info'}>
              {match.groupme_unpublish_reason || 'This match was unpublished from GroupMe.'}
              {match.groupme_last_event_id ? ` Previous GroupMe event ID: ${match.groupme_last_event_id}.` : ''}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Opponent</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>{match.opponent_name || 'Opponent TBD'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Match</Typography>
              <Typography variant="body2">{optionLabel(matchSiteTypeOptions, match.match_site_type)} • {optionLabel(matchClassificationOptions, match.match_classification)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Match Status</Typography>
              <Typography variant="body2">{optionLabel(matchStatusOptions, match.match_status)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Occurrence</Typography>
              <Typography variant="body2">{occurrence?.occurrence_state || 'Unknown'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Results</Typography>
              <Typography variant="body2">{occurrence?.result_completeness || 'Awaiting Scores'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Lineup</Typography>
              <Typography variant="body2">{optionLabel(lineupStatusOptions, match.lineup_status)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Availability</Typography>
              <Typography variant="body2">{summary.available}/{summary.total} Available • {summary.maybe} Maybe • {summary.notAvailable} Unavailable</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Match Time</Typography>
              <Typography variant="body2">{formatTime(match.match_time, timeFormat, match) || 'Time not set'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Warmup</Typography>
              <Typography variant="body2">{formatTime(match.warmup_time, timeFormat, match) || 'Not set'}</Typography>
            </Box>
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <Typography variant="caption" color="text.secondary">Facility / Location</Typography>
              <Typography variant="body2">{matchLocation(match, lookups)}</Typography>
            </Box>
            {match.match_status === 'Completed' && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" color="text.secondary">Result</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{matchResultLabel(result)}</Typography>
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {timeline.map((step) => (
              <Chip
                key={step.label}
                size="small"
                icon={step.done ? <CheckCircleIcon /> : undefined}
                label={step.label}
                color={step.done ? 'success' : 'default'}
                variant={step.done ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>

          {summary.availablePlayers.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {availableGroups.men.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Men ({availableGroups.men.length})</Typography>
                  {renderMatchAvailabilityChips(availableGroups.men, 'men')}
                </Box>
              )}
              {availableGroups.women.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Women ({availableGroups.women.length})</Typography>
                  {renderMatchAvailabilityChips(availableGroups.women, 'women')}
                </Box>
              )}
              {availableGroups.unmatched.length > 0 && (
                <Box sx={{ gridColumn: { lg: availableGroups.men.length || availableGroups.women.length ? '1 / -1' : 'auto' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Unmatched ({availableGroups.unmatched.length})</Typography>
                  {renderMatchAvailabilityChips(availableGroups.unmatched, 'unmatched')}
                </Box>
              )}
            </Box>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" onClick={() => onOpenLineup?.(match.id, { teamId: match.team_id })}>
              Lineup Builder
            </Button>
            {!readOnly && match.match_status !== 'Completed' && ['Draft', 'Published'].includes(match.lineup_status) && (
              <GroupMeLineupSend
                matchId={match.id}
                onSent={async (message) => {
                  await loadEvents();
                  setSnack({ severity: 'success', message });
                }}
              />
            )}
            <Button size="small" variant="outlined" onClick={() => onOpenAvailability?.(match.team_id)}>
              Player Availability
            </Button>
            {result?.result_status === 'Confirmed' && !readOnly ? (
              <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => void markMatchScoresSubmitted(match, result)}>
                Mark Scores Submitted
              </Button>
            ) : result?.result_status !== 'Submitted' ? (
              <Button size="small" variant="outlined" onClick={() => setScoreEditorMatchId(match.id)}>
                Enter Scores
              </Button>
            ) : null}
            {!readOnly && (match.groupme_event_id ? (
              <>
                <Button size="small" variant="outlined" startIcon={<ReplayIcon />} disabled={groupMeActionKey === `refresh:match:${match.id}`} onClick={() => void refreshRowGroupMeRsvps({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match })}>
                  Refresh RSVPs
                </Button>
                <Button size="small" variant="outlined" startIcon={<PublishIcon />} disabled={groupMeActionKey === `update:match:${match.id}`} onClick={() => void updateRowGroupMeEvent({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match })}>
                  Update GroupMe
                </Button>
                <Button size="small" variant="outlined" color="warning" startIcon={<DeleteIcon />} disabled={groupMeActionKey === `unpublish:match:${match.id}`} onClick={() => void unpublishRowFromGroupMe({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match })}>
                  Unpublish
                </Button>
              </>
            ) : (
              <Tooltip title={groupMePublishEligibility({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match }).reason}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<PublishIcon />} disabled={!groupMePublishEligibility({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match }).eligible || groupMeActionKey === `publish:match:${match.id}`} onClick={() => void publishRowToGroupMe({ kind: 'match', key: `match:${match.id}`, date: match.match_date, time: match.match_time, team_id: match.team_id, record: match })}>
                    Publish
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const rowTitle = (row: UnifiedEventRow): string => (
    row.kind === 'match' ? matchTitle(row.record) : row.record.title
  );

  const rowTypeLabel = (row: UnifiedEventRow): string => (
    row.kind === 'match' ? 'Match' : eventTypeLabel(row.record.event_type)
  );

  const rowLocation = (row: UnifiedEventRow): string => (
    row.kind === 'match' ? matchLocation(row.record, lookups) : facilityOrLocation(row.record)
  );

  const rowSummary = (row: UnifiedEventRow): string => {
    if (row.kind === 'match') return matchSummaryLabel(row.record, matchAvailability[row.record.id], matchResults[row.record.id], matchOccurrence[row.record.id]);
    const attendanceRows = attendanceByEventId.get(row.record.id) || [];
    if (attendanceRows.length === 0) return '-';
    const summary = summarizeGroupMeOnlyRsvps(attendanceRows.map(attendanceSummaryInput));
    const guests = summarizeEventGuests(attendanceRows);
    return `${summary.goingTotal} Going · ${guests.guests} Guests · ${guests.totalAttending} Total`;
  };

  const groupMeStaleLinkReason = (row: UnifiedEventRow): string | null => {
    const reason = row.record.groupme_unpublish_reason || '';
    if (!row.record.groupme_event_id && row.record.groupme_unpublished_at && reason.includes('deleted in GroupMe')) {
      return reason;
    }
    return null;
  };

  const groupMePublishEligibility = (row: UnifiedEventRow): { eligible: boolean; reason: string } => {
    if (row.kind === 'team-event' && row.record.audience_scope === 'workspace') return { eligible: false, reason: 'Workspace events use Player Portal RSVPs' };
    if (row.record.groupme_event_id) return { eligible: false, reason: 'Already published' };
    const team = teamById.get(row.team_id);
    if (!team?.groupme_group_id) return { eligible: false, reason: 'No GroupMe group mapped' };
    const groupMeTime = row.kind === 'match' && row.record.groupme_event_time_source === 'warmup_time'
      ? row.record.warmup_time
      : row.time;
    if (!row.date || !groupMeTime) return { eligible: false, reason: 'Missing date/time' };
    if (row.kind === 'team-event' && row.record.status === 'Cancelled') return { eligible: false, reason: 'Cancelled' };
    if (row.kind === 'match' && row.record.match_status === 'Cancelled') return { eligible: false, reason: 'Cancelled' };
    return { eligible: true, reason: 'Publish to GroupMe' };
  };

  const publishableRows = useMemo(
    () => unifiedRows.filter((row) => groupMePublishEligibility(row).eligible),
    [unifiedRows, teamById],
  );

  const callGroupMeEventAction = async (action: 'publish' | 'publish_all' | 'refresh' | 'update' | 'unpublish', items: Array<{ kind: 'match' | 'event'; id: string }>, trigger?: string, reason?: string) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Sign in again before using GroupMe event actions.');
    const response = await fetch('/api/groupme/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action, items, trigger, reason }),
    });
    const result = await response.json() as GroupMeEventActionResult;
    if (!response.ok) throw new Error(result.error || 'GroupMe event action failed.');
    return result;
  };

  const publishRowToGroupMe = async (row: UnifiedEventRow) => {
    const key = `publish:${row.key}`;
    setGroupMeActionKey(key);
    try {
      const result = await callGroupMeEventAction('publish', [{ kind: row.kind === 'match' ? 'match' : 'event', id: row.record.id }]);
      const failed = result.summary?.failed || 0;
      const skipped = result.summary?.skipped || 0;
      if (failed > 0 || skipped > 0) {
        const reason = result.results?.find((item) => item.status !== 'published')?.reason;
        setSnack({ severity: failed ? 'error' : 'warning', message: reason || 'GroupMe publish did not complete.' });
      } else {
        setSnack({ severity: 'success', message: 'Published to GroupMe and refreshed RSVPs.' });
      }
      await loadEvents();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to publish to GroupMe.' });
    } finally {
      setGroupMeActionKey(null);
    }
  };

  const publishAllVisibleToGroupMe = async () => {
    if (publishableRows.length === 0) {
      setSnack({ severity: 'warning', message: 'No visible unpublished events are eligible to publish to GroupMe.' });
      return;
    }
    if (!window.confirm(`Publish ${publishableRows.length} unpublished event${publishableRows.length === 1 ? '' : 's'} to GroupMe?`)) return;
    setGroupMeActionKey('publish-all');
    try {
      const result = await callGroupMeEventAction('publish_all', publishableRows.map((row) => ({ kind: row.kind === 'match' ? 'match' : 'event', id: row.record.id })));
      const summary = result.summary || { published: 0, skipped: 0, failed: 0, updated: 0, refreshed: 0 };
      const issues = (result.results || []).filter((item) => item.status === 'skipped' || item.status === 'failed').slice(0, 3).map((item) => item.reason).filter(Boolean);
      setSnack({
        severity: summary.failed > 0 ? 'warning' : 'success',
        message: `${summary.published} published, ${summary.skipped} skipped, ${summary.failed} failed${issues.length ? ` (${issues.join('; ')})` : ''}.`,
      });
      await loadEvents();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to publish visible events to GroupMe.' });
    } finally {
      setGroupMeActionKey(null);
    }
  };

  const refreshRowGroupMeRsvps = async (row: UnifiedEventRow, trigger = 'manual') => {
    const key = `refresh:${row.key}`;
    setGroupMeActionKey(key);
    try {
      const result = await callGroupMeEventAction('refresh', [{ kind: row.kind === 'match' ? 'match' : 'event', id: row.record.id }], trigger);
      const failed = result.summary?.failed || 0;
      const skipped = result.summary?.skipped || 0;
      if (failed > 0 || skipped > 0) {
        const reason = result.results?.find((item) => item.status !== 'refreshed')?.reason;
        setSnack({ severity: failed ? 'error' : 'warning', message: reason || 'Unable to refresh GroupMe RSVPs. Showing last synced data.' });
      } else {
        setSnack({ severity: 'success', message: 'RSVPs refreshed.' });
      }
      await loadEvents();
    } catch (error) {
      setSnack({ severity: 'warning', message: error instanceof Error ? error.message : 'Unable to refresh GroupMe RSVPs. Showing last synced data.' });
    } finally {
      setGroupMeActionKey(null);
    }
  };

  const updateRowGroupMeEvent = async (row: UnifiedEventRow) => {
    const key = `update:${row.key}`;
    setGroupMeActionKey(key);
    try {
      const result = await callGroupMeEventAction('update', [{ kind: row.kind === 'match' ? 'match' : 'event', id: row.record.id }]);
      const failed = result.summary?.failed || 0;
      const skipped = result.summary?.skipped || 0;
      if (failed > 0 || skipped > 0) {
        const reason = result.results?.find((item) => item.status !== 'updated')?.reason;
        setSnack({ severity: failed ? 'error' : 'warning', message: reason || 'Unable to update GroupMe event.' });
      } else {
        setSnack({ severity: 'success', message: 'GroupMe event updated and RSVPs refreshed.' });
      }
      await loadEvents();
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to update GroupMe event.' });
    } finally {
      setGroupMeActionKey(null);
    }
  };

  const unpublishRowFromGroupMe = async (row: UnifiedEventRow, options: { confirm?: boolean; reason?: string; reload?: boolean } = {}) => {
    if (!row.record.groupme_event_id) {
      setSnack({ severity: 'warning', message: 'This event is not linked to GroupMe.' });
      return false;
    }
    if (options.confirm !== false && !window.confirm(`Unpublish ${rowTitle(row)} from GroupMe? CourtCaptain will keep the event, but the GroupMe event will be removed. Republishing later will create a new GroupMe event.`)) {
      return false;
    }
    const key = `unpublish:${row.key}`;
    setGroupMeActionKey(key);
    try {
      const result = await callGroupMeEventAction(
        'unpublish',
        [{ kind: row.kind === 'match' ? 'match' : 'event', id: row.record.id }],
        undefined,
        options.reason || 'Unpublished from CourtCaptain.',
      );
      const failed = result.summary?.failed || 0;
      const skipped = result.summary?.skipped || 0;
      if (failed > 0 || skipped > 0) {
        const reason = result.results?.find((item) => item.status !== 'unpublished')?.reason;
        setSnack({ severity: failed ? 'error' : 'warning', message: reason || 'Unable to unpublish GroupMe event.' });
        return false;
      }
      setSnack({ severity: 'success', message: 'Unpublished from GroupMe. Republishing will create a new GroupMe event.' });
      if (options.reload !== false) await loadEvents();
      return true;
    } catch (error) {
      setSnack({ severity: 'error', message: error instanceof Error ? error.message : 'Unable to unpublish GroupMe event.' });
      return false;
    } finally {
      setGroupMeActionKey(null);
    }
  };

  useEffect(() => {
    if (readOnly || !detailOpen || !selectedEvent?.groupme_event_id) return;
    const row: UnifiedEventRow = {
      kind: 'team-event',
      key: `team-event:${selectedEvent.id}`,
      date: selectedEvent.event_date,
      time: selectedEvent.start_time,
      team_id: selectedEvent.team_id,
      record: selectedEvent,
    };
    void refreshRowGroupMeRsvps(row, 'event_open');
  }, [detailOpen, selectedEvent?.id]);

  const openUnifiedRow = (row: UnifiedEventRow) => {
    if (row.kind === 'match') {
      onOpenMatchDetail?.(row.record.id);
      return;
    }
    openDetail(row.record);
  };

  const editUnifiedRow = (row: UnifiedEventRow) => {
    if (row.kind === 'match') {
      onEditMatch?.(row.record.id);
      return;
    }
    if (onEditEvent) {
      onEditEvent(row.record.id, 'team-events');
      return;
    }
    openEdit(row.record);
  };

  const renderCollapsedGroupMeActions = (row: UnifiedEventRow) => {
    if (readOnly) return null;
    const title = rowTitle(row);
    const iconButtonSx = {
      width: 34,
      height: 34,
      border: '1px solid',
      borderColor: 'divider',
      '&:hover': { borderColor: 'currentColor' },
    };
    const actionSlotSx = { width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
    const groupMeActionShellSx = {
      display: 'grid',
      gridTemplateColumns: '64px repeat(3, 34px)',
      gap: 0.5,
      alignItems: 'center',
      justifyContent: { xs: 'start', md: 'end' },
      minWidth: 184,
    };

    if (row.record.groupme_event_id) {
      return (
        <Box
          sx={groupMeActionShellSx}
          data-collapsed-groupme-actions="linked"
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textAlign: 'right' }}>
            GroupMe
          </Typography>
          <Tooltip title="Refresh RSVPs from GroupMe" arrow>
            <Box component="span" sx={actionSlotSx}>
              <IconButton
                size="small"
                color="primary"
                aria-label={`Refresh RSVPs from GroupMe for ${title}`}
                disabled={groupMeActionKey === `refresh:${row.key}`}
                onClick={() => void refreshRowGroupMeRsvps(row)}
                sx={iconButtonSx}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Update event in GroupMe" arrow>
            <Box component="span" sx={actionSlotSx}>
              <IconButton
                size="small"
                color="primary"
                aria-label={`Update ${title} in GroupMe`}
                disabled={groupMeActionKey === `update:${row.key}`}
                onClick={() => void updateRowGroupMeEvent(row)}
                sx={iconButtonSx}
              >
                <PublishIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title="Unpublish event from GroupMe" arrow>
            <Box component="span" sx={actionSlotSx}>
              <IconButton
                size="small"
                color="warning"
                aria-label={`Unpublish ${title} from GroupMe`}
                disabled={groupMeActionKey === `unpublish:${row.key}`}
                onClick={() => void unpublishRowFromGroupMe(row)}
                sx={iconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      );
    }

    const publishEligibility = groupMePublishEligibility(row);
    if (!publishEligibility.eligible) {
      return (
        <Box
          sx={groupMeActionShellSx}
          data-collapsed-groupme-actions="unpublished"
          aria-hidden="true"
        />
      );
    }

    return (
      <Box
        sx={groupMeActionShellSx}
        data-collapsed-groupme-actions="publishable"
      >
        <Box aria-hidden="true" />
        <Box aria-hidden="true" sx={actionSlotSx} />
        <Tooltip title="Publish to GroupMe" arrow>
          <Box component="span" sx={actionSlotSx}>
            <IconButton
              size="small"
              color="primary"
              aria-label={`Publish ${title} to GroupMe`}
              disabled={groupMeActionKey === `publish:${row.key}`}
              onClick={() => void publishRowToGroupMe(row)}
              sx={iconButtonSx}
            >
              <PublishIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
        <Box aria-hidden="true" sx={actionSlotSx} />
      </Box>
    );
  };

  const renderBulkEditorCell = (event: TeamEventRecord, field: TeamEventBulkField) => {
    const value = bulkValue(event, field);
    const error = bulkErrors[teamEventBulkCellKey(event.id, field)];
    const changed = isBulkCellChanged(event, field);
    const commonSx = {
      minWidth: field === 'description' || field === 'notes' ? 260 : field === 'title' || field === 'location_text' ? 220 : 150,
      bgcolor: changed ? 'warning.light' : undefined,
      '& .MuiInputBase-input': { py: 0.75, fontSize: 13 },
    };
    if (field === 'event_type') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Practice'} inputProps={{ 'data-team-event-bulk-cell': 'true' }} onChange={(change) => updateBulkCell(event, field, change.target.value)}>
            {teamEventTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'status') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Scheduled'} inputProps={{ 'data-team-event-bulk-cell': 'true' }} onChange={(change) => updateBulkCell(event, field, change.target.value)}>
            {eventStatusOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'facility_id') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value} inputProps={{ 'data-team-event-bulk-cell': 'true' }} onChange={(change) => updateBulkCell(event, field, change.target.value)}>
            <MenuItem value="">No Facility</MenuItem>
            {lookups.facilities.map((facility) => <MenuItem key={facility.value} value={facility.value}>{facility.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    return (
      <TextField
        size="small"
        value={value}
        error={Boolean(error)}
        helperText={error || ''}
        type={field === 'event_date' ? 'date' : field === 'start_time' || field === 'end_time' ? 'time' : 'text'}
        multiline={field === 'description' || field === 'notes'}
        minRows={field === 'description' || field === 'notes' ? 2 : undefined}
        InputLabelProps={field === 'event_date' ? { shrink: true } : undefined}
        sx={commonSx}
        onKeyDown={(keyEvent) => {
          if (keyEvent.key !== 'Tab' && keyEvent.key !== 'Enter') return;
          const inputs = Array.from(document.querySelectorAll<HTMLElement>('input[data-team-event-bulk-cell="true"], textarea[data-team-event-bulk-cell="true"], [role="combobox"] input[data-team-event-bulk-cell="true"]'));
          const index = inputs.indexOf(keyEvent.currentTarget);
          const next = inputs[index + (keyEvent.shiftKey ? -1 : 1)];
          if (next) {
            keyEvent.preventDefault();
            next.focus();
          }
        }}
        inputProps={{ 'data-team-event-bulk-cell': 'true' }}
        onChange={(change) => updateBulkCell(event, field, change.target.value)}
      />
    );
  };

  const renderMatchBulkEditorCell = (match: MatchEventRecord, field: MatchBulkField) => {
    const value = matchBulkValue(match, field);
    const error = bulkErrors[matchBulkCellKey(match.id, field)];
    const changed = isMatchBulkCellChanged(match, field);
    const commonSx = {
      minWidth: field === 'notes_summary' ? 260 : field === 'opponent_tennis_record_roster_url' || field === 'opponent_name' ? 220 : 150,
      bgcolor: changed ? 'warning.light' : undefined,
      '& .MuiInputBase-input': { py: 0.75, fontSize: 13 },
    };
    if (field === 'match_site_type') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Unknown'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateMatchBulkCell(match, field, event.target.value)}>
            {matchSiteTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'match_status') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'Draft'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateMatchBulkCell(match, field, event.target.value)}>
            {matchStatusOptions.map((option) => <MenuItem key={option.value} value={option.value} disabled={option.value === 'Scores Entered' || option.value === 'Completed'}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'match_classification') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value || 'regular_season'} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateMatchBulkCell(match, field, event.target.value)}>
            {matchClassificationOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    if (field === 'facility_id') {
      return (
        <FormControl fullWidth size="small" error={Boolean(error)} sx={commonSx}>
          <Select value={value} inputProps={{ 'data-match-bulk-cell': 'true' }} onChange={(event) => updateMatchBulkCell(match, field, event.target.value)}>
            <MenuItem value="">None</MenuItem>
            {lookups.facilities.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </FormControl>
      );
    }
    return (
      <TextField
        size="small"
        value={value}
        error={Boolean(error)}
        helperText={error || ''}
        type={field === 'match_date' ? 'date' : field === 'match_time' || field === 'warmup_time' ? 'time' : 'text'}
        multiline={field === 'notes_summary'}
        minRows={field === 'notes_summary' ? 2 : undefined}
        InputLabelProps={field === 'match_date' ? { shrink: true } : undefined}
        sx={commonSx}
        onKeyDown={(event) => {
          if (event.key !== 'Tab' && event.key !== 'Enter') return;
          const inputs = Array.from(document.querySelectorAll<HTMLElement>('input[data-match-bulk-cell="true"], textarea[data-match-bulk-cell="true"], [role="combobox"] input[data-match-bulk-cell="true"]'));
          const index = inputs.indexOf(event.currentTarget);
          const next = inputs[index + (event.shiftKey ? -1 : 1)];
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }}
        inputProps={{ 'data-match-bulk-cell': 'true' }}
        onChange={(event) => updateMatchBulkCell(match, field, event.target.value)}
      />
    );
  };

  // Practice Rotation is a child view of Event Detail. Let it render first once
  // the action has selected an event, while retaining Event Detail for Back.
  if (detailOpen && selectedEvent && !practiceEvent) {
    const source = groupMeSourceByEventId.get(selectedEvent.id);
    const row: UnifiedEventRow = {
      kind: 'team-event',
      key: `team-event:${selectedEvent.id}`,
      date: selectedEvent.event_date,
      time: selectedEvent.start_time,
      team_id: selectedEvent.team_id,
      record: selectedEvent,
    };
    const closeDetail = () => {
      setDetailOpen(false);
      window.history.pushState({}, '', '/team-events');
    };
    const renderGroupMeDetail = () => {
      if (!source && !selectedEvent.groupme_event_id) {
        return (
          <Stack spacing={1}>
            {selectedEvent.groupme_unpublished_at && (
              <Alert severity={selectedEvent.groupme_unpublish_reason?.includes('deleted in GroupMe') ? 'warning' : 'info'}>
                {selectedEvent.groupme_unpublish_reason || 'This event was unpublished from GroupMe.'}
                {selectedEvent.groupme_last_event_id ? ` Previous GroupMe event ID: ${selectedEvent.groupme_last_event_id}.` : ''}
              </Alert>
            )}
            <Alert severity="info">This Team Event is not linked to a GroupMe event.</Alert>
            {!readOnly && (
              <Tooltip title={groupMePublishEligibility(row).reason}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<PublishIcon />}
                    disabled={!groupMePublishEligibility(row).eligible || groupMeActionKey === `publish:${row.key}`}
                    onClick={() => void publishRowToGroupMe(row)}
                  >
                    Publish to GroupMe
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        );
      }

      const sourceParticipants = source ? participantsBySourceId.get(source.id) || [] : [];
      const attendanceRows = attendanceByEventId.get(selectedEvent.id) || [];
      const detailParticipants: RsvpDisplayRow[] = attendanceRows.length > 0
        ? attendanceRows.map(attendanceAsGroupable)
        : sourceParticipants;
      const summary = attendanceRows.length > 0
        ? summarizeGroupMeOnlyRsvps(attendanceRows.map(attendanceSummaryInput))
        : summarizeGroupMeOnlyRsvps(sourceParticipants);
      const guestSummary = summarizeEventGuests(attendanceRows);
      const detailGoingGroups = groupRsvpParticipantsByGender(
        detailParticipants.filter((participant) => participant.latest_rsvp_status === 'Going'),
      );
      const teamPlayerCount = eventAudiencePlayerCount(selectedEvent);
      const sourceKey = source?.id || selectedEvent.id;
      const manualDraft = manualDraftForSource(sourceKey);

      return (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Event RSVPs</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {renderRsvpSummaryChips(summary, teamPlayerCount, detailGoingGroups.unmatched.length, attendanceRows.length > 0 ? guestSummary : undefined, selectedEvent.allow_maybe_rsvp !== false)}
              {source?.parsed_event_date_source === 'title' && <Chip size="small" variant="outlined" color="success" label="Date parsed from title" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              GroupMe linked · {selectedEvent.groupme_event_id || source?.groupme_event_id || 'No GroupMe event ID'}{source?.sync_status ? ` · ${source.sync_status}` : ''}
            </Typography>
            {source?.source_updated_at && (
              <Typography variant="caption" color="text.secondary">Last refreshed {formattedTimestamp(source.source_updated_at)}</Typography>
            )}
            {source?.sync_error && <Alert severity="warning">{source.sync_error}</Alert>}
            <Stack direction="row" spacing={1} justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" startIcon={<ReplayIcon />} disabled={readOnly || groupMeActionKey === `refresh:${row.key}`} onClick={() => void refreshRowGroupMeRsvps(row)} sx={{ alignSelf: 'flex-start' }}>
                  Refresh RSVPs
                </Button>
                <Button variant="outlined" startIcon={<PublishIcon />} disabled={readOnly || groupMeActionKey === `update:${row.key}`} onClick={() => void updateRowGroupMeEvent(row)} sx={{ alignSelf: 'flex-start' }}>
                  Update GroupMe
                </Button>
                <Button variant="outlined" color="warning" startIcon={<DeleteIcon />} disabled={readOnly || groupMeActionKey === `unpublish:${row.key}`} onClick={() => void unpublishRowFromGroupMe(row)} sx={{ alignSelf: 'flex-start' }}>
                  Unpublish
                </Button>
              </Stack>
              {selectedEvent.event_type === 'Practice' && (
                <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setPracticeSourceId(selectedEvent.id)} sx={{ alignSelf: 'flex-start' }}>
                  Practice Rotation
                </Button>
              )}
            </Stack>
            {detailGoingGroups.men.length + detailGoingGroups.women.length + detailGoingGroups.unmatched.length === 0 && (
              <Typography variant="body2" color="text.secondary">No players are currently marked Going.</Typography>
            )}
            {renderGuestChips(guestSummary.goingGuestEntries)}
            {!readOnly && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(27, 94, 32, 0.03)' }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Add/Change RSVPs</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(150px, 0.4fr) minmax(220px, 1fr) auto' }, gap: 1, alignItems: 'center' }}>
                    <FormControl size="small">
                      <InputLabel>Player</InputLabel>
                      <Select label="Player" value={manualDraft.playerId} onChange={(change) => updateManualRsvpDraft(sourceKey, { playerId: change.target.value })}>
                        <MenuItem value="">Select player</MenuItem>
                        {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>Status</InputLabel>
                      <Select label="Status" value={manualDraft.status} onChange={(change) => updateManualRsvpDraft(sourceKey, { status: change.target.value as ManualRsvpStatus })}>
                        <MenuItem value="Going">Going</MenuItem>
                        {selectedEvent.allow_maybe_rsvp !== false && <MenuItem value="Maybe">Maybe</MenuItem>}
                        <MenuItem value="Not Going">Not Going</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField size="small" label="Note" value={manualDraft.note} onChange={(change) => updateManualRsvpDraft(sourceKey, { note: change.target.value })} placeholder="Optional" />
                    <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => source ? void saveManualRsvp(source) : void saveTeamEventAttendance(selectedEvent)}>
                      Save RSVP
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            )}
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>RSVP</TableCell>
                  <TableCell>People</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(['Going', 'Maybe', 'Not Going', 'Unknown'] as const).map((status) => {
                  const rows = detailParticipants
                    .filter((participant) => participant.latest_rsvp_status === status)
                    .sort((a, b) => participantName(a).localeCompare(participantName(b), undefined, { sensitivity: 'base' }));
                  if (rows.length === 0) return null;
                  return (
                    <TableRow key={status}>
                      <TableCell sx={{ width: 170 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip size="small" label={status === 'Maybe' && selectedEvent.allow_maybe_rsvp === false ? 'Needs Confirmation' : status} sx={rsvpSummaryChipSx(status === 'Going' ? 'going' : status === 'Maybe' ? 'maybe' : status === 'Not Going' ? 'notGoing' : 'unmatched')} />
                          <Tooltip title={`Copy ${status} names`}>
                            <IconButton size="small" aria-label={`Copy ${status} names`} onClick={() => void copyRsvpNames(status, rows)}>
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const groups = groupRsvpParticipantsByGender(rows);
                          return (
                            <Stack spacing={1}>
                              {groups.men.length > 0 && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Men ({groups.men.length})</Typography>
                                  {renderDetailRsvpChips(groups.men, 'men')}
                                </Box>
                              )}
                              {groups.women.length > 0 && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Women ({groups.women.length})</Typography>
                                  {renderDetailRsvpChips(groups.women, 'women')}
                                </Box>
                              )}
                              {groups.unmatched.length > 0 && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Unmatched ({groups.unmatched.length})</Typography>
                                  {renderDetailRsvpChips(groups.unmatched, 'unmatched')}
                                </Box>
                              )}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Stack>
        </Paper>
      );
    };

    return (
      <Stack spacing={2.5}>
        <BreadcrumbTrail
          items={buildBreadcrumbTrail(teamEventCanonicalContext(selectedEvent.title, `/team-events/${selectedEvent.id}`))}
          onNavigate={(item) => {
            if (item.path === '/team-events') closeDetail();
          }}
        />
        <Box>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={closeDetail}>
            Back to Team Events
          </Button>
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Event Detail</Typography>
          <Typography variant="body2" color="text.secondary">Review event details, attendance, and GroupMe communication.</Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: 2.5, borderLeft: `6px solid ${lookupTeamAccentColor(lookups, selectedEvent.team_id)}` }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" useFlexGap>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedEvent.title}</Typography>
                  <Chip size="small" label={eventTypeLabel(selectedEvent.event_type)} />
                  <Chip size="small" variant="outlined" label={selectedEvent.audience_scope === 'workspace' ? 'Workspace Event' : 'Team Event'} />
                  <Chip size="small" color={statusColor(selectedEvent.status)} label={selectedEvent.status} />
                  {selectedEvent.recurrence_series_id && <Chip size="small" color="info" label="Recurring" />}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedEvent.audience_scope === 'workspace'
                    ? `All eligible players in ${lookupLabel(lookups, 'workspaces', selectedEvent.workspace_id || '') || 'this workspace'}`
                    : `${lookupLabel(lookups, 'teams', selectedEvent.team_id)}${teamById.get(selectedEvent.team_id)?.season_label ? ` · ${teamById.get(selectedEvent.team_id)?.season_label}` : ''}`}
                </Typography>
              </Box>
              {!readOnly && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={() => { if (onEditEvent) onEditEvent(selectedEvent.id, 'team-event-detail'); else openEdit(selectedEvent); }}>Edit Event</Button>
                  <Button variant="outlined" startIcon={<CancelIcon />} color="warning" onClick={() => void updateStatus(selectedEvent, 'Cancelled')}>Cancel Event</Button>
                  <Button variant="outlined" startIcon={<CheckCircleIcon />} color="success" onClick={() => void updateStatus(selectedEvent, 'Completed')}>Mark Completed</Button>
                  <Button variant="outlined" startIcon={<DeleteIcon />} color="error" onClick={() => void softDeleteEvent(selectedEvent)}>Delete</Button>
                  {selectedEvent.recurrence_series_id && (
                    <Button startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={() => void softDeleteFutureSeries(selectedEvent)}>Delete Future Series</Button>
                  )}
                </Stack>
              )}
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <Box><Typography variant="caption" color="text.secondary">Date</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(localEventDate(selectedEvent))}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Start</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTime(selectedEvent.start_time, timeFormat, selectedEvent) || 'Not set'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">End</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTime(selectedEvent.end_time, timeFormat, selectedEvent) || 'Not set'}</Typography></Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Facility / Location</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{facilityOrLocation(selectedEvent)}</Typography>
            </Box>
            <CalendarPublicationPanel
              kind="event"
              id={selectedEvent.id}
              readOnly={readOnly}
              onMessage={(severity, message) => setSnack({ severity, message })}
            />
            {selectedEvent.description && (
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{selectedEvent.description}</Typography>
              </Box>
            )}
            {selectedEvent.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography variant="body2">{selectedEvent.notes}</Typography>
              </Box>
            )}
          </Stack>
        </Paper>
        {renderGroupMeDetail()}
        <Snackbar open={Boolean(snack)} autoHideDuration={4500} onClose={() => setSnack(null)}>
          {snack ? <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
        </Snackbar>
      </Stack>
    );
  }

  if (practiceEvent) {
    const source = groupMeSourceByEventId.get(practiceEvent.id);
    return (
      <PracticeRotationPlanner
        event={{
          id: practiceEvent.id,
          team_id: practiceEvent.team_id,
          title: practiceEvent.title,
          event_date: practiceEvent.event_date,
          workspace_id: source?.workspace_id || teamById.get(practiceEvent.team_id)?.workspace_id || null,
          audience_scope: practiceEvent.audience_scope || 'selected_teams',
          groupme_only_rsvp_event_id: source?.id || null,
        }}
        participants={attendanceByEventId.get(practiceEvent.id) || []}
        teamPlayerCount={eventAudiencePlayerCount(practiceEvent)}
        lookups={lookups}
        readOnly={readOnly}
        manualRsvpDraft={manualDraftForSource(practiceEvent.id)}
        onManualRsvpDraftChange={(patch) => updateManualRsvpDraft(practiceEvent.id, patch)}
        onSaveRsvp={() => void saveTeamEventAttendance(practiceEvent)}
        onReplayRsvps={source ? () => void replayGroupMeEventRsvps(source) : undefined}
        replayRsvpsDisabled={!source?.groupme_event_id || replayingSourceId === source?.id}
        onBack={() => setPracticeSourceId(null)}
        setSnack={setSnack}
      />
    );
  }

  return (
    <Stack spacing={2.5}>
      <DraftRecoveryDialog
        open={Boolean(eventDraftPersistence.recoverableDraft)}
        updatedAt={eventDraftPersistence.recoverableDraft?.updatedAt}
        onRestore={() => {
          const restored = eventDraftPersistence.restoreDraft();
          if (!restored) return;
          setForm(restored.form);
          setRecurrence(restored.recurrence);
        }}
        onDiscard={eventDraftPersistence.discardDraft}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Team Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage matches, practices, social events, team meetings, and other team activities.
          </Typography>
        </Box>
        {!readOnly && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ sm: 'flex-end' }} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={openBulkEditor} disabled={bulkEditableRowCount === 0} sx={{ whiteSpace: 'nowrap' }}>
              {isMatchBulkMode ? 'Bulk Edit Matches' : 'Bulk Edit Team Events'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PublishIcon />}
              onClick={() => void publishAllVisibleToGroupMe()}
              disabled={groupMeActionKey === 'publish-all' || publishableRows.length === 0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Publish Unpublished to GroupMe
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => onAddEvent ? onAddEvent() : setAddTypeDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
              Add Event
            </Button>
          </Stack>
        )}
      </Stack>

      {shouldShowCaptainScopeEmpty(teamFilters, defaultCaptainTeamIds, teamScopeMode) && <Alert severity="info">You aren't assigned as captain or co-captain of a team. Select a team, or choose Clear to view all authorized teams.</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: dateRangePreset === 'custom' ? 'repeat(6, 1fr)' : 'repeat(4, 1fr)' }, gap: 1.5 }}>
          <SeasonTeamMultiSelect lookups={lookups} value={teamFilters} onChange={(ids) => { setTeamFilters(ids); setTeamScopeMode('explicit'); }} allLabel={teamScopeMode === 'default' && defaultCaptainTeamIds.length === 0 ? 'Select a team' : 'All Teams'} size="small" />
          <FormControl size="small" fullWidth>
            <InputLabel>Event Type</InputLabel>
            <Select label="Event Type" value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              {eventTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Date Range</InputLabel>
            <Select label="Date Range" value={dateRangePreset} onChange={(event) => setDateRangePreset(event.target.value as DateRangePreset)}>
              {dateRangeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          {dateRangePreset === 'custom' && (
            <>
              <TextField size="small" label="From" type="date" value={dateFromFilter} onChange={(event) => setDateFromFilter(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="To" type="date" value={dateToFilter} onChange={(event) => setDateToFilter(event.target.value)} InputLabelProps={{ shrink: true }} />
            </>
          )}
          <Button size="small" variant="outlined" onClick={clearFilters}>Clear</Button>
        </Box>
      </Paper>

      {loading && <LinearProgress />}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5, p: 2 }}>
          {unifiedRows.map((row) => {
            const expanded = expandedEventId === row.key;
            return (
              <Paper key={row.key} variant="outlined" sx={{ overflow: 'hidden', borderLeft: `6px solid ${lookupTeamAccentColor(lookups, row.team_id)}` }}>
                <Stack spacing={1} sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Tooltip title={expanded ? 'Collapse details' : 'Expand details'}>
                      <IconButton
                        size="small"
                        aria-label={expanded ? `Collapse details for ${rowTitle(row)}` : `Expand details for ${rowTitle(row)}`}
                        onClick={() => toggleExpanded(row.key)}
                      >
                        {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatDate(row.date)}{formatTime(row.time, timeFormat, row.record) ? ` ${formatTime(row.time, timeFormat, row.record)}` : ''}</Typography>
                      <Typography variant="body2">{row.kind === 'team-event' && row.record.audience_scope === 'workspace' ? 'Workspace Event' : lookupLabel(lookups, 'teams', row.team_id)}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" color="text.secondary">{rowTypeLabel(row)} • {rowTitle(row)}</Typography>
                        {groupMeStaleLinkReason(row) && (
                          <Tooltip title={`${groupMeStaleLinkReason(row)}${row.record.groupme_last_event_id ? ` Previous GroupMe event ID: ${row.record.groupme_last_event_id}.` : ''}`} arrow>
                            <Chip size="small" color="warning" label="GroupMe missing" />
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                  <Typography variant="body2">{rowLocation(row)}</Typography>
                  <Typography variant="body2">Summary: {rowSummary(row)}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" startIcon={<VisibilityIcon />} onClick={() => openUnifiedRow(row)} fullWidth>Open</Button>
                    {!readOnly && <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => editUnifiedRow(row)} fullWidth>Edit</Button>}
                  </Stack>
                  {renderCollapsedGroupMeActions(row)}
                </Stack>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Divider />
                  {row.kind === 'match'
                    ? renderMatchExpandedPanel(row.record)
                    : renderAttendancePanel(row.record, groupMeSourceByEventId.get(row.record.id), attendanceByEventId.get(row.record.id) || [])}
                </Collapse>
              </Paper>
            );
          })}
          {unifiedRows.length === 0 && !loading && <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No events found.</Typography>}
        </Box>
        <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table size="small" sx={{ minWidth: 1320 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" aria-label="Expand row" />
                <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, width: 320 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unifiedRows.map((row) => {
                const expanded = expandedEventId === row.key;
                return (
                  <Fragment key={row.key}>
                    <TableRow hover sx={{ verticalAlign: 'top' }}>
                      <TableCell padding="checkbox" sx={{ borderLeft: `6px solid ${lookupTeamAccentColor(lookups, row.team_id)}` }}>
                        <Tooltip title={expanded ? 'Collapse details' : 'Expand details'}>
                          <IconButton
                            size="small"
                            aria-label={expanded ? `Collapse details for ${rowTitle(row)}` : `Expand details for ${rowTitle(row)}`}
                            aria-expanded={expanded}
                            onClick={() => toggleExpanded(row.key)}
                          >
                            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography component="span" variant="body2">{rowTitle(row)}</Typography>
                          {groupMeStaleLinkReason(row) && (
                            <Tooltip title={`${groupMeStaleLinkReason(row)}${row.record.groupme_last_event_id ? ` Previous GroupMe event ID: ${row.record.groupme_last_event_id}.` : ''}`} arrow>
                              <Chip size="small" color="warning" label="GroupMe missing" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDate(row.date)}</TableCell>
                      <TableCell>{formatTime(row.time, timeFormat, row.record) || 'Time not set'}</TableCell>
                      <TableCell>{row.kind === 'team-event' && row.record.audience_scope === 'workspace' ? 'Workspace Event' : lookupLabel(lookups, 'teams', row.team_id)}</TableCell>
                      <TableCell>{rowTypeLabel(row)}</TableCell>
                      <TableCell>{rowLocation(row)}</TableCell>
                      <TableCell>{rowSummary(row)}</TableCell>
                      <TableCell align="right" sx={{ width: 320, minWidth: 320 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" useFlexGap flexWrap="nowrap">
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
                            <Button size="small" variant="text" startIcon={<VisibilityIcon />} onClick={() => openUnifiedRow(row)}>Open</Button>
                            {!readOnly && <Button size="small" variant="text" startIcon={<EditIcon />} onClick={() => editUnifiedRow(row)}>Edit</Button>}
                          </Stack>
                          {!readOnly && <Divider orientation="vertical" flexItem />}
                          {renderCollapsedGroupMeActions(row)}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          {row.kind === 'match'
                            ? renderMatchExpandedPanel(row.record)
                            : renderAttendancePanel(row.record, groupMeSourceByEventId.get(row.record.id), attendanceByEventId.get(row.record.id) || [])}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
              {unifiedRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No events found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={addTypeDialogOpen} onClose={() => setAddTypeDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 1 }}>
            {eventTypeOptions.map((option) => (
              <Button
                key={option.value}
                variant={option.value === 'Match' ? 'contained' : 'outlined'}
                onClick={() => {
                  setAddTypeDialogOpen(false);
                  if (option.value === 'Match') {
                    onAddMatch?.();
                    return;
                  }
                  openCreateWithType(option.value as Exclude<TeamEventType, 'Match'>);
                }}
                sx={{ justifyContent: 'flex-start' }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddTypeDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkEditorOpen} onClose={cancelBulkEditor} fullWidth maxWidth="xl">
        <DialogTitle>{isMatchBulkMode ? 'Bulk Edit Matches' : 'Bulk Edit Team Events'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {isMatchBulkMode
                  ? `Editing ${bulkEditableMatches.length} visible match${bulkEditableMatches.length === 1 ? '' : 'es'}`
                  : `Editing ${bulkEditableTeamEvents.length} visible team event${bulkEditableTeamEvents.length === 1 ? '' : 's'}`}
              </Typography>
              <Chip size="small" color={bulkPendingCellCount > 0 ? 'warning' : 'default'} label={`${bulkPendingCellCount} pending change${bulkPendingCellCount === 1 ? '' : 's'}`} />
            </Stack>
            <TableContainer sx={{ maxHeight: '64vh', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {isMatchBulkMode ? (
                <Table size="small" stickyHeader sx={{ minWidth: 2100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, minWidth: 190 }}>Team</TableCell>
                      {matchBulkFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 800 }}>{matchBulkLabels[field]}</TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 800, minWidth: 90 }}>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkEditableMatches.map((match) => {
                      const changedCells = matchBulkFields.filter((field) => isMatchBulkCellChanged(match, field)).length;
                      return (
                        <TableRow key={match.id} hover sx={{ verticalAlign: 'top', bgcolor: changedCells > 0 ? 'action.hover' : undefined }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{lookupLabel(lookups, 'teams', match.team_id)}</Typography>
                            <Typography variant="caption" color="text.secondary" component="div">{teamById.get(match.team_id)?.season_label || ''}</Typography>
                          </TableCell>
                          {matchBulkFields.map((field) => (
                            <TableCell key={field}>
                              {renderMatchBulkEditorCell(match, field)}
                            </TableCell>
                          ))}
                          <TableCell>
                            {changedCells > 0 ? <Chip size="small" color="warning" label={`${changedCells} changed`} /> : <Chip size="small" variant="outlined" label="Clean" />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {bulkEditableMatches.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={matchBulkFields.length + 2}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            No visible matches are editable.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, minWidth: 180 }}>Team</TableCell>
                      {teamEventBulkFields.map((field) => (
                        <TableCell key={field} sx={{ fontWeight: 800 }}>{teamEventBulkLabels[field]}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkEditableTeamEvents.map((event) => (
                      <TableRow key={event.id} hover sx={{ verticalAlign: 'top' }}>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{lookupLabel(lookups, 'teams', event.team_id)}</Typography>
                            {groupMeSourceByEventId.get(event.id) && <Chip size="small" variant="outlined" label="GroupMe linked" sx={{ alignSelf: 'flex-start' }} />}
                          </Stack>
                        </TableCell>
                        {teamEventBulkFields.map((field) => (
                          <TableCell key={field}>
                            {renderBulkEditorCell(event, field)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {bulkEditableTeamEvents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={teamEventBulkFields.length + 1}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            No visible team events are editable.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelBulkEditor} disabled={bulkEditorSaving}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={bulkEditorSaving || bulkPendingCellCount === 0} onClick={() => void saveBulkEditor()}>
            {bulkEditorSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Team Event' : 'Add Team Event'}</DialogTitle>
        <DialogContent>
          <DraftSavedIndicator savedAt={eventDraftPersistence.draftSavedAt} />
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Team</InputLabel>
                <Select label="Team" value={form.team_id} onChange={(event) => changeTeam(event.target.value)} required>
                  {teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.team_name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select label="Event Type" value={form.event_type} onChange={(event) => updateForm({ event_type: event.target.value as EventForm['event_type'] })} required>
                  {teamEventTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status} onChange={(event) => updateForm({ status: event.target.value as TeamEventStatus })} required>
                  {eventStatusOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField label="Title" value={form.title} onChange={(event) => updateForm({ title: event.target.value })} required fullWidth />
            <TextField label="Description" value={form.description} onChange={(event) => updateForm({ description: event.target.value })} multiline minRows={3} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                value={form.event_date}
                onChange={(event) => {
                  const nextDate = event.target.value;
                  updateForm({ event_date: nextDate });
                  if (!editing && nextDate) {
                    const parsedDate = dateFromIso(nextDate);
                    setRecurrence((current) => ({
                      ...current,
                      recurrence_start_date: nextDate,
                      recurrence_end_date: current.recurrence_end_date || nextDate,
                      days_of_week: current.days_of_week.length > 0 ? current.days_of_week : [parsedDate.getDay()],
                      day_of_month: parsedDate.getDate(),
                    }));
                  }
                }}
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
              <TextField label="Start Time" type="time" value={form.start_time} onChange={(event) => updateForm({ start_time: event.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="End Time" type="time" value={form.end_time} onChange={(event) => updateForm({ end_time: event.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Facility</InputLabel>
                <Select label="Facility" value={form.facility_id} onChange={(event) => updateForm({ facility_id: event.target.value })}>
                  <MenuItem value="">No Facility</MenuItem>
                  {lookups.facilities.map((facility) => <MenuItem key={facility.value} value={facility.value}>{facility.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Location Text" value={form.location_text} onChange={(event) => updateForm({ location_text: event.target.value })} fullWidth />
              <EventTimezoneField facilityId={form.facility_id} location={form.location_text} value={form.timezone} manual={form.timezone_manual_override} onChange={(timezone, manual) => updateForm({ timezone, timezone_manual_override: manual })} />
            </Box>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(27, 94, 32, 0.03)' }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>GroupMe Publishing</Typography>
                <TextField
                  label="GroupMe Event Title"
                  value={form.groupme_event_title}
                  onChange={(event) => updateForm({ groupme_event_title: event.target.value })}
                  helperText="Optional. Leave blank to use the CourtCaptain event title."
                  fullWidth
                />
                <TextField
                  label="GroupMe Event Description"
                  value={form.groupme_event_description}
                  onChange={(event) => updateForm({ groupme_event_description: event.target.value })}
                  helperText="Optional external text that will be sent to GroupMe. Internal notes are not published."
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label="GroupMe Event Image URL"
                  value={form.groupme_event_image_url}
                  onChange={(event) => updateForm({ groupme_event_image_url: event.target.value })}
                  helperText="Optional. Upload an image or paste a GroupMe image URL."
                  fullWidth
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    disabled={groupMeImageUploading || !form.team_id}
                  >
                    {groupMeImageUploading ? 'Uploading Image' : 'Upload Image'}
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/gif"
                      onChange={(event) => {
                        void uploadGroupMeImage(event.target.files?.[0] || null);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                  {form.groupme_event_image_url ? (
                    <Button variant="text" onClick={() => updateForm({ groupme_event_image_url: '' })}>
                      Clear Image
                    </Button>
                  ) : null}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: form.groupme_reminder_mode === 'custom' ? 'minmax(180px, 0.8fr) repeat(2, minmax(180px, 1fr))' : 'minmax(180px, 0.8fr) minmax(220px, 1fr)' }, gap: 1.5, alignItems: 'center' }}>
                  <FormControl fullWidth>
                    <InputLabel>GroupMe Reminders</InputLabel>
                    <Select
                      label="GroupMe Reminders"
                      value={form.groupme_reminder_mode}
                      onChange={(event) => updateForm({ groupme_reminder_mode: event.target.value as EventForm['groupme_reminder_mode'] })}
                    >
                      <MenuItem value="team_default">Use Team Defaults</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                  {form.groupme_reminder_mode === 'custom' ? (
                    [0, 1].map((index) => {
                      const reminders = normalizeGroupMeReminders(form.groupme_custom_reminders);
                      return (
                        <FormControl key={index} fullWidth>
                          <InputLabel>{index === 0 ? 'First Reminder' : 'Second Reminder'}</InputLabel>
                          <Select
                            label={index === 0 ? 'First Reminder' : 'Second Reminder'}
                            value={reminders[index] == null ? '' : String(reminders[index])}
                            onChange={(event) => updateFormReminder(index, event.target.value)}
                          >
                            <MenuItem value="">No reminder</MenuItem>
                            {groupMeReminderOptions.map((option) => (
                              <MenuItem key={option.value} value={String(option.value)} disabled={reminders.includes(option.value) && reminders[index] !== option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Team default: {groupMeReminderSummary(teamById.get(form.team_id)?.default_groupme_event_reminders)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
            {!editing && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Recurrence</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Repeat</InputLabel>
                      <Select
                        label="Repeat"
                        value={recurrence.recurrence_type}
                        onChange={(event) => updateRecurrence({ recurrence_type: event.target.value as EventRecurrenceType })}
                      >
                        {recurrenceTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Repeat Every"
                      type="number"
                      value={recurrence.repeat_interval}
                      onChange={(event) => updateRecurrence({ repeat_interval: Math.max(1, Number(event.target.value || 1)) })}
                      inputProps={{ min: 1 }}
                      disabled={recurrence.recurrence_type === 'Does Not Repeat'}
                      fullWidth
                    />
                    <TextField
                      label="Ends On"
                      type="date"
                      value={recurrence.recurrence_end_date}
                      onChange={(event) => updateRecurrence({ recurrence_end_date: event.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={recurrence.recurrence_type === 'Does Not Repeat'}
                      fullWidth
                    />
                  </Box>
                  {recurrence.recurrence_type !== 'Does Not Repeat' && (
                    <>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: recurrence.recurrence_type === 'Monthly' ? 'repeat(2, 1fr)' : '1fr' }, gap: 2 }}>
                        <TextField
                          label="Recurrence Start Date"
                          type="date"
                          value={recurrence.recurrence_start_date}
                          onChange={(event) => updateRecurrence({ recurrence_start_date: event.target.value })}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                        {recurrence.recurrence_type === 'Monthly' && (
                          <TextField
                            label="Day of Month"
                            type="number"
                            value={recurrence.day_of_month}
                            onChange={(event) => updateRecurrence({ day_of_month: Math.min(31, Math.max(1, Number(event.target.value || 1))) })}
                            inputProps={{ min: 1, max: 31 }}
                            fullWidth
                          />
                        )}
                      </Box>
                      {recurrence.recurrence_type === 'Weekly' && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Days of Week</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {daysOfWeekOptions.map((day) => (
                              <FormControlLabel
                                key={day.value}
                                control={
                                  <Checkbox
                                    checked={recurrence.days_of_week.includes(day.value)}
                                    onChange={(event) => {
                                      const nextDays = event.target.checked
                                        ? [...recurrence.days_of_week, day.value]
                                        : recurrence.days_of_week.filter((value) => value !== day.value);
                                      updateRecurrence({ days_of_week: nextDays.sort((a, b) => a - b) });
                                    }}
                                  />
                                }
                                label={day.label}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      <Alert severity={occurrenceDates.length > maxOccurrences ? 'error' : 'info'}>
                        This will create {occurrenceDates.length} event{occurrenceDates.length === 1 ? '' : 's'}.
                      </Alert>
                    </>
                  )}
                </Stack>
              </Paper>
            )}
            <TextField label="Notes" value={form.notes} onChange={(event) => updateForm({ notes: event.target.value })} multiline minRows={3} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          {!readOnly && <Button variant="contained" onClick={() => void saveEvent()}>{!editing && recurrence.recurrence_type !== 'Does Not Repeat' ? `Create ${occurrenceDates.length} Events` : 'Save Event'}</Button>}
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          window.history.pushState({}, '', '/team-events');
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Event Detail</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <BreadcrumbTrail
                items={buildBreadcrumbTrail(teamEventCanonicalContext(selectedEvent.title, `/team-events/${selectedEvent.id}`))}
                onNavigate={(item) => {
                  if (item.path === '/team-events') {
                    setDetailOpen(false);
                    window.history.pushState({}, '', '/team-events');
                  }
                }}
              />
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedEvent.title}</Typography>
                  <Chip size="small" label={eventTypeLabel(selectedEvent.event_type)} />
                  <Chip size="small" color={statusColor(selectedEvent.status)} label={selectedEvent.status} />
                  {selectedEvent.recurrence_series_id && <Chip size="small" color="info" label="Recurring" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {lookupLabel(lookups, 'teams', selectedEvent.team_id)}{teamById.get(selectedEvent.team_id)?.season_label ? ` • ${teamById.get(selectedEvent.team_id)?.season_label}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                <Box><Typography variant="caption" color="text.secondary">Date</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatDate(localEventDate(selectedEvent))}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Start</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTime(selectedEvent.start_time, timeFormat, selectedEvent) || 'Not set'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">End</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTime(selectedEvent.end_time, timeFormat, selectedEvent) || 'Not set'}</Typography></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Facility / Location</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{facilityOrLocation(selectedEvent)}</Typography>
              </Box>
              <CalendarPublicationPanel
                kind="event"
                id={selectedEvent.id}
                readOnly={readOnly}
                onMessage={(severity, message) => setSnack({ severity, message })}
              />
              {(() => {
                const source = groupMeSourceByEventId.get(selectedEvent.id);
                if (!source && !selectedEvent.groupme_event_id) {
                  const row: UnifiedEventRow = { kind: 'team-event', key: `team-event:${selectedEvent.id}`, date: selectedEvent.event_date, time: selectedEvent.start_time, team_id: selectedEvent.team_id, record: selectedEvent };
                  return (
                    <Stack spacing={1}>
                      {selectedEvent.groupme_unpublished_at && (
                        <Alert severity={selectedEvent.groupme_unpublish_reason?.includes('deleted in GroupMe') ? 'warning' : 'info'}>
                          {selectedEvent.groupme_unpublish_reason || 'This event was unpublished from GroupMe.'}
                          {selectedEvent.groupme_last_event_id ? ` Previous GroupMe event ID: ${selectedEvent.groupme_last_event_id}.` : ''}
                        </Alert>
                      )}
                      <Alert severity="info">This Team Event is not linked to a GroupMe event.</Alert>
                      {!readOnly && (
                        <Tooltip title={groupMePublishEligibility(row).reason}>
                          <span>
                            <Button
                              variant="outlined"
                              startIcon={<PublishIcon />}
                              disabled={!groupMePublishEligibility(row).eligible || groupMeActionKey === `publish:${row.key}`}
                              onClick={() => void publishRowToGroupMe(row)}
                            >
                              Publish to GroupMe
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  );
                }
                const sourceParticipants = source ? participantsBySourceId.get(source.id) || [] : [];
                const attendanceRows = attendanceByEventId.get(selectedEvent.id) || [];
                const detailParticipants: RsvpDisplayRow[] = attendanceRows.length > 0
                  ? attendanceRows.map(attendanceAsGroupable)
                  : sourceParticipants;
                const summary = attendanceRows.length > 0
                  ? summarizeGroupMeOnlyRsvps(attendanceRows.map(attendanceSummaryInput))
                  : summarizeGroupMeOnlyRsvps(sourceParticipants);
                const guestSummary = summarizeEventGuests(attendanceRows);
                const detailGoingGroups = groupRsvpParticipantsByGender(
                  detailParticipants.filter((participant) => participant.latest_rsvp_status === 'Going'),
                );
                const teamPlayerCount = eventAudiencePlayerCount(selectedEvent);
                const sourceKey = source?.id || selectedEvent.id;
                const manualDraft = manualDraftForSource(sourceKey);
                const row: UnifiedEventRow = { kind: 'team-event', key: `team-event:${selectedEvent.id}`, date: selectedEvent.event_date, time: selectedEvent.start_time, team_id: selectedEvent.team_id, record: selectedEvent };
                return (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Event RSVPs</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {renderRsvpSummaryChips(summary, teamPlayerCount, detailGoingGroups.unmatched.length, attendanceRows.length > 0 ? guestSummary : undefined, selectedEvent.allow_maybe_rsvp !== false)}
                        {source?.parsed_event_date_source === 'title' && <Chip size="small" variant="outlined" color="success" label="Date parsed from title" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        GroupMe linked · {selectedEvent.groupme_event_id || source?.groupme_event_id || 'No GroupMe event ID'}{source?.sync_status ? ` · ${source.sync_status}` : ''}
                      </Typography>
                      {source?.source_updated_at && (
                        <Typography variant="caption" color="text.secondary">Last refreshed {formattedTimestamp(source.source_updated_at)}</Typography>
                      )}
                      {source?.sync_error && <Alert severity="warning">{source.sync_error}</Alert>}
                      {renderGuestChips(guestSummary.goingGuestEntries)}
                      <Stack direction="row" spacing={1} justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="outlined"
                            startIcon={<ReplayIcon />}
                            disabled={readOnly || groupMeActionKey === `refresh:${row.key}`}
                            onClick={() => void refreshRowGroupMeRsvps(row)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Refresh RSVPs
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<PublishIcon />}
                            disabled={readOnly || groupMeActionKey === `update:${row.key}`}
                            onClick={() => void updateRowGroupMeEvent(row)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Update GroupMe
                          </Button>
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<DeleteIcon />}
                            disabled={readOnly || groupMeActionKey === `unpublish:${row.key}`}
                            onClick={() => void unpublishRowFromGroupMe(row)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Unpublish
                          </Button>
                        </Stack>
                        {selectedEvent.event_type === 'Practice' && (
                          <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => setPracticeSourceId(selectedEvent.id)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Practice Rotation
                          </Button>
                        )}
                      </Stack>
                      {detailGoingGroups.men.length + detailGoingGroups.women.length + detailGoingGroups.unmatched.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No players are currently marked Going.
                        </Typography>
                      )}
                      {!readOnly && (
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(27, 94, 32, 0.03)' }}>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Add/Change RSVPs</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) minmax(150px, 0.4fr) minmax(220px, 1fr) auto' }, gap: 1, alignItems: 'center' }}>
                              <FormControl size="small">
                                <InputLabel>Player</InputLabel>
                                <Select
                                  label="Player"
                                  value={manualDraft.playerId}
                                  onChange={(change) => updateManualRsvpDraft(sourceKey, { playerId: change.target.value })}
                                >
                                  <MenuItem value="">Select player</MenuItem>
                                  {lookups.players.map((player) => <MenuItem key={player.value} value={player.value}>{player.label}</MenuItem>)}
                                </Select>
                              </FormControl>
                              <FormControl size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                  label="Status"
                                  value={manualDraft.status}
                                  onChange={(change) => updateManualRsvpDraft(sourceKey, { status: change.target.value as ManualRsvpStatus })}
                                >
                                  <MenuItem value="Going">Going</MenuItem>
                                  {selectedEvent.allow_maybe_rsvp !== false && <MenuItem value="Maybe">Maybe</MenuItem>}
                                  <MenuItem value="Not Going">Not Going</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                size="small"
                                label="Note"
                                value={manualDraft.note}
                                onChange={(change) => updateManualRsvpDraft(sourceKey, { note: change.target.value })}
                                placeholder="Optional"
                              />
                              <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => source ? void saveManualRsvp(source) : void saveTeamEventAttendance(selectedEvent)}>
                                Save RSVP
                              </Button>
                            </Box>
                          </Stack>
                        </Paper>
                      )}
                      <Divider />
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>RSVP</TableCell>
                            <TableCell>People</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(['Going', 'Maybe', 'Not Going', 'Unknown'] as const).map((status) => {
                            const rows = detailParticipants
                              .filter((participant) => participant.latest_rsvp_status === status)
                              .sort((a, b) => participantName(a).localeCompare(participantName(b), undefined, { sensitivity: 'base' }));
                            if (rows.length === 0) return null;
                            return (
                              <TableRow key={status}>
                                <TableCell sx={{ width: 170 }}>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip
                                      size="small"
                                      label={status === 'Maybe' && selectedEvent.allow_maybe_rsvp === false ? 'Needs Confirmation' : status}
                                      sx={rsvpSummaryChipSx(status === 'Going' ? 'going' : status === 'Maybe' ? 'maybe' : status === 'Not Going' ? 'notGoing' : 'unmatched')}
                                    />
                                    <Tooltip title={`Copy ${status} names`}>
                                      <IconButton
                                        size="small"
                                        aria-label={`Copy ${status} names`}
                                        onClick={() => void copyRsvpNames(status, rows)}
                                      >
                                        <ContentCopyIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const groups = groupRsvpParticipantsByGender(rows);
                                    return (
                                      <Stack spacing={1}>
                                        {groups.men.length > 0 && (
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Men ({groups.men.length})</Typography>
                                            {renderDetailRsvpChips(groups.men, 'men')}
                                          </Box>
                                        )}
                                        {groups.women.length > 0 && (
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Women ({groups.women.length})</Typography>
                                            {renderDetailRsvpChips(groups.women, 'women')}
                                          </Box>
                                        )}
                                        {groups.unmatched.length > 0 && (
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>Unmatched ({groups.unmatched.length})</Typography>
                                            {renderDetailRsvpChips(groups.unmatched, 'unmatched')}
                                          </Box>
                                        )}
                                      </Stack>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Stack>
                  </Paper>
                );
              })()}
              {selectedEvent.description && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedEvent.description}</Typography>
                </Box>
              )}
              {selectedEvent.notes && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{selectedEvent.notes}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {selectedEvent && !readOnly && (
            <>
              <Button startIcon={<EditIcon />} onClick={() => { setDetailOpen(false); if (onEditEvent) onEditEvent(selectedEvent.id, 'team-event-detail'); else openEdit(selectedEvent); }}>Edit Event</Button>
              <Button startIcon={<CancelIcon />} color="warning" onClick={() => void updateStatus(selectedEvent, 'Cancelled')}>Cancel Event</Button>
              <Button startIcon={<CheckCircleIcon />} color="success" onClick={() => void updateStatus(selectedEvent, 'Completed')}>Mark Completed</Button>
              <Button startIcon={<DeleteIcon />} color="error" onClick={() => void softDeleteEvent(selectedEvent)}>Delete</Button>
              {selectedEvent.recurrence_series_id && (
                <Button startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={() => void softDeleteFutureSeries(selectedEvent)}>
                  Delete Future Series
                </Button>
              )}
            </>
          )}
          <Button onClick={() => {
            setDetailOpen(false);
            window.history.pushState({}, '', '/team-events');
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {scoreEditorMatch && (
        <MatchResults
          match={scoreEditorMatch}
          onSaved={loadEvents}
          readOnly={readOnly}
          editorOpen={Boolean(scoreEditorMatch)}
          onEditorClose={() => setScoreEditorMatchId(null)}
          hideInlineSummary
        />
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={4500} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Stack>
  );
}
